/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes.
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * Cart refresh for minicart
 */
function refreshCart() {
  fetch(`${window.themeVariables.routes.cartUrl}.js`).then(
    async (response2) => {
      const cartContent = await response2.json();
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:refresh", {
          bubbles: true,
          detail: {
            cart: cartContent,
            openMiniCart: true,
          },
        })
      );
    }
  );
}

/**
 * Handle Price Per Square Feet Filtering
 */
function handlePriceAndPricePerSqFtFilters() {
  const pricePerSquareFtFilters = document.querySelectorAll(
    '[name="filter.p.m.custom.price_per_sqft"]'
  );
  const rugsProductTypeFilter = document.querySelector(
    '[name="filter.p.product_type"][value="RUGS"]'
  );
  const priceInputFilters = document.querySelectorAll(
    ".price-range__input-group input"
  );

  // Handle of Price Filter
  if (priceInputFilters && rugsProductTypeFilter) {
    priceInputFilters.forEach((priceInputFilter) => {
      priceInputFilter.addEventListener("change", (e) => {
        if (e.target.value) {
          // Click checked price per square ft filters
          pricePerSquareFtFilters.forEach((pricePerSquareFtFilter) => {
            if (pricePerSquareFtFilter.checked) {
              pricePerSquareFtFilter.click();
            }
          });

          // Select Rug Product if not checked
          if (!rugsProductTypeFilter.checked) {
            rugsProductTypeFilter.click();
          }
        }
      });
    });
  }

  // Handle Selecting of Price Per Square Feet Filters
  if (pricePerSquareFtFilters) {
    pricePerSquareFtFilters.forEach((pricePerSquareFtFilter) => {
      pricePerSquareFtFilter.addEventListener("click", (e) => {
        // Clear price input filters when pricePerSquareFtFilter is clicked
        priceInputFilters.forEach((priceInputFilter) => {
          priceInputFilter.value = ""; // Clear the input field
        });
      });
    });
  }
}

/**
 * Product card hover-image Escape dismissal (WCAG 1.4.13)
 *
 * The primary/secondary image swap on product cards is pure CSS (:hover /
 * :focus-within in section-product.css), so there's no JS-tracked "open" state
 * to toggle off. Escape instead adds a class to whichever wrapper currently
 * matches :hover or :focus-within, and higher-specificity CSS rules
 * (.is-dismissed) force the secondary image back off without requiring the
 * pointer or keyboard focus to move. The class is removed again once the
 * pointer or focus actually leaves the wrapper, so the swap works normally
 * on the next hover/focus. (Cleared on "leave" rather than "enter" because a
 * few browsers can synthesize a mouseover when a CSS visibility swap changes
 * which element sits under an otherwise-stationary cursor — clearing on entry
 * risks that synthetic event immediately undoing the Escape dismissal.)
 */
document.addEventListener("keydown", (event) => {
  if (event.key !== "Escape") return;

  document
    .querySelectorAll(".product-item__image-wrapper--multiple")
    .forEach((wrapper) => {
      if (wrapper.matches(":hover") || wrapper.matches(":focus-within")) {
        wrapper.classList.add("is-dismissed");
      }
    });
});

// mouseleave doesn't bubble, but capture-phase dispatch still happens for
// every element the pointer exits, so delegating via capture still works.
document.addEventListener(
  "mouseleave",
  (event) => {
    const wrapper = event.target.closest?.(
      ".product-item__image-wrapper--multiple"
    );
    if (wrapper) wrapper.classList.remove("is-dismissed");
  },
  true
);

document.addEventListener("focusout", (event) => {
  const wrapper = event.target.closest(
    ".product-item__image-wrapper--multiple"
  );
  if (wrapper && !wrapper.contains(event.relatedTarget)) {
    wrapper.classList.remove("is-dismissed");
  }
});

/**
 * Focus management for facet updates (WCAG 2.4.3)
 *
 * Any facet change — clearing a filter tag, "Clear all", or checking/
 * unchecking a filter checkbox — causes <product-facet> to replace
 * #facet-filters' entire innerHTML with a freshly-fetched fragment,
 * destroying whichever control was actually interacted with. The browser is
 * then left with nothing to move focus to and drops it to <body>; the next
 * Tab press then restarts from the top of the page, which reads as a focus
 * order that no longer matches where the user visually was. There's no flag
 * on the resulting DOM mutation (or on the facet:criteria-changed event,
 * dispatched identically by both kinds of change) distinguishing which one
 * happened or which control triggered it, so we record that ourselves and
 * consume it once the replacement lands:
 *  - clear-filters clicks have no equivalent control left in the new DOM, so
 *    they fall back to the "Filters" heading.
 *  - checkbox changes restore focus to the same option (matched by name +
 *    value) in the new DOM, since that option normally still exists there —
 *    falling back to the heading only if that option is no longer part of
 *    the facet results after the change (e.g. it became unavailable given
 *    the new filter combination).
 */
let facetFocusRestoreTarget = null;

document.addEventListener("click", (event) => {
  if (event.target.closest('[data-action="clear-filters"]')) {
    facetFocusRestoreTarget = { type: "heading" };
  }
});

document.addEventListener("change", (event) => {
  const checkbox = event.target.closest(
    '#facet-filters-form input[type="checkbox"][name^="filter."]'
  );
  if (checkbox) {
    facetFocusRestoreTarget = {
      type: "checkbox",
      name: checkbox.name,
      value: checkbox.value,
    };
  }
});

const facetFilters = document.querySelector("facet-filters");
const facetCallback = function (mutationsList, observer) {
  handlePriceAndPricePerSqFtFilters();

  if (!facetFocusRestoreTarget) return;
  const target = facetFocusRestoreTarget;
  facetFocusRestoreTarget = null;

  let elementToFocus = null;
  if (target.type === "checkbox") {
    elementToFocus = document.querySelector(
      `#facet-filters-form input[type="checkbox"][name="${CSS.escape(
        target.name
      )}"][value="${CSS.escape(target.value)}"]`
    );
  }
  if (!elementToFocus) {
    elementToFocus = document.querySelector("#facet-filters .drawer__title");
  }
  if (elementToFocus) elementToFocus.focus();
};

const facetObserver = new MutationObserver(facetCallback);
const facetConfig = { attributes: true, childList: true, subtree: true };
if (facetFilters) {
  facetObserver.observe(facetFilters, facetConfig);

  const isFacetFiltersDrawer = () => facetFilters.classList.contains("drawer");

  facetFilters.toggleAttribute(
    "inert",
    isFacetFiltersDrawer() && !facetFilters.hasAttribute("open")
  );
  facetFilters.addEventListener("openable-element:open", () => {
    facetFilters.removeAttribute("inert");
  });
  facetFilters.addEventListener("openable-element:close", () => {
    if (!isFacetFiltersDrawer()) return;
    facetFilters.setAttribute("inert", "");
  });
}

(async () => {
  const pagePath = window.location.pathname;
  const qsParams = new URLSearchParams(location.href.split("?")[1]);

  /**
   * CLEAR CART ON LOGOUT / IF LOGGED OUT
   * Note: Customer session (retail or trade) is not expected to time out / expire
   */
  const logoutLink = document.querySelector(".link-bar__link[data-no-instant]");
  if (logoutLink !== null) {
    logoutLink.addEventListener("click", function (event) {
      let cartCookieValue = getExpiredCartConfirmation();
      if (cartCookieValue === undefined) {
        event.preventDefault();
        deleteCookie("cart");
        logoutLink.click();
      } else {
        // Revert the setting of this window property
        //  - this is to avoid failing to clear the cart next time
        window.cartCookieExpiration = undefined;
      }
    });
  }

  function getExpiredCartConfirmation() {
    return window.cartCookieExpiration;
  }

  function setCookie(key) {
    document.cookie = key + "=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;";
    // Also set expiration date where we can check it later
    window.cartCookieExpiration = "Expires=Thu, 01 Jan 1970 00:00:01 GMT";
  }

  function deleteCookie(name) {
    setCookie(name);
  }

  const loggedInB2B =
    document.getElementById("header-loggedin-mobile__b2b") ||
    document.getElementById("header-loggedin-desktop__b2b");

  if (loggedInB2B !== null) {
    removeNonB2BSamples();
  }

  // get current cart
  async function removeNonB2BSamples() {
    let updates = {};
    let cartContents = await fetch(window.Shopify.routes.root + "cart.js")
      .then((response) => response.json())
      .then((data) => {
        return data;
      });

    // check cart for non-B2B samples
    if (cartContents && cartContents.items) {
      Array.from(cartContents.items, (item) => {
        if (item.sku.endsWith("_S")) {
          updates[item.variant_id] = 0;
        }
      });
    }

    // remove any non-B2B samples
    if (Object.keys(updates).length > 0) {
      fetch(window.Shopify.routes.root + "cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ updates }),
      })
        .then(async (response) => {
          let responseJson = await response.json();
          //refresh cart
          document.documentElement.dispatchEvent(
            new CustomEvent("cart:refresh", {
              bubbles: true,
              detail: {
                cart: responseJson,
              },
            })
          );
        })
        .catch((error) => {
          console.error("Error:", error);
        });
    }
  }

  // create a debounce method
  const debounce = (mainFunction, delay) => {
    let timer;

    return function (...args) {
      clearTimeout(timer);

      timer = setTimeout(() => {
        mainFunction(...args);
      }, delay);
    };
  };

  /*
   * HOMEPAGE HERO BANNER LOGIC
   */

  // Homepage banner mobile logic
  function homepageHeroBannerMobileFunctionality() {
    const homepageBanner = document.querySelector(
      ".shopify-section--hero-banner"
    );

    if (!homepageBanner) return;

    function setMobileHeights() {
      let bannerContent = document.querySelector(
        ".shopify-section--hero-banner .content-box"
      );
      if (!bannerContent) {
        return;
      }
      let bannerContentHeight = bannerContent.offsetHeight - 1; // slight overlapp to prevent whitespace
      let calculatedAssetHeight = `calc(100vh - ${bannerContentHeight}px`;

      homepageBanner.style.setProperty("--asset-height", calculatedAssetHeight);
    }

    window.addEventListener("DOMContentLoaded", function () {
      if (window.innerWidth <= 768) {
        setMobileHeights();
      }
    });

    window.addEventListener("resize", function () {
      if (window.innerWidth <= 768) {
        setMobileHeights();
      }
    });
  }

  // Homepage banner fixed position logic
  function homepageHeroBannerFixedPositionFunctionality() {
    const homepageBanner = document.querySelector(
      ".shopify-section--hero-banner"
    );
    const fixedSlider = document.querySelector(
      ".shopify-section--hero-banner .fixed-slideshow"
    );

    if (!(homepageBanner && fixedSlider)) return;

    // update slideshow visibility based on scroll position
    function updateHomepageBannerVisibilty() {
      let scrollY = window.scrollY;
      let slideshowHeight = fixedSlider.offsetHeight;
      let sliderInViewport = scrollY <= slideshowHeight;
      let sliderIsVisible = fixedSlider.classList.contains("slideshow-visible");
      let sliderIsHidden = fixedSlider.classList.contains("slideshow-hidden");

      if (!sliderInViewport && sliderIsVisible) {
        hideSlider(fixedSlider);
      } else if (sliderInViewport && sliderIsHidden) {
        showSlider(fixedSlider);
      }
    }

    function hideSlider(slider) {
      slider.classList.add("slideshow-hidden");
      slider.classList.remove("slideshow-visible");
    }

    function showSlider(slider) {
      slider.classList.remove("slideshow-hidden");
      slider.classList.add("slideshow-visible");
    }

    const debouncedVisibility = debounce(updateHomepageBannerVisibilty, 50);

    document.addEventListener("scroll", function () {
      debouncedVisibility();
    });
  }

  // Homepage banner promobar logic
  function homepageHeroBannerPromobarFunctionality() {
    const homepageBanner = document.querySelector(
      ".shopify-section--hero-banner"
    );
    const homepageSection = document.querySelector(
      ".shopify-section--hero-banner .section"
    );
    const promobar = document.querySelector(
      ".shopify-section--announcement-bar"
    );

    if (!(homepageBanner && promobar)) return;

    function updatePromobarVisibility() {
      let scrollY = window.scrollY;
      let slideshowHeight = homepageSection.offsetHeight;
      let sliderInViewport = scrollY <= slideshowHeight;
      let promobarIsVisible = promobar.classList.contains("promobar-visible");
      let promobarIsHidden = promobar.classList.contains("promobar-hidden");

      if (sliderInViewport && promobarIsVisible) {
        hidePromobar();
      } else if (!sliderInViewport && promobarIsHidden) {
        showPromobar();
      }
    }

    function hidePromobar() {
      promobar.classList.add("promobar-hidden");
      promobar.classList.remove("promobar-visible");
    }

    function showPromobar() {
      promobar.classList.remove("promobar-hidden");
      promobar.classList.add("promobar-visible");
      promobar.classList.add("fade-in-top");
    }

    const debouncedPromobarVisibility = debounce(updatePromobarVisibility, 100);

    promobar.classList.add("promobar-hidden");

    document.addEventListener("scroll", function () {
      debouncedPromobarVisibility();
    });
  }

  // Homepage nav bar logic
  function homepageHeroBannerNavFunctionality() {
    const homepageBanner = document.querySelector(
      ".shopify-section--hero-banner"
    );
    const homepageSection = document.querySelector(
      ".shopify-section--hero-banner .section"
    );
    const mainHeader = document.querySelector(".main-header");
    const storeHeader = document.querySelector("store-header");
    const transparentHeader = document.querySelector(
      ".transparent-main-header"
    );

    if (!(homepageBanner && transparentHeader)) return;

    function updateHeaderVisibility() {
      let scrollY = window.scrollY;
      let slideshowHeight = homepageSection.offsetHeight;
      let sliderInViewport = scrollY <= slideshowHeight;
      let transparentHeaderIsVisible = transparentHeader.classList.contains(
        "transparent-main-header-visible"
      );
      let transparentHeaderIsHidden = transparentHeader.classList.contains(
        "transparent-main-header-hidden"
      );

      if (sliderInViewport && transparentHeaderIsHidden) {
        hideOriginalHeader();
      } else if (!sliderInViewport && transparentHeaderIsVisible) {
        showOriginalHeader();
      }
    }

    function hideOriginalHeader() {
      mainHeader.classList.add("main-header-hidden");
      mainHeader.classList.remove("main-header-visible");

      mainHeader.classList.remove("fade-out-top");
      mainHeader.classList.add("fade-in-top");

      transparentHeader.classList.add("transparent-main-header-visible");
      transparentHeader.classList.remove("transparent-main-header-hidden");
    }

    function showOriginalHeader() {
      mainHeader.classList.remove("main-header-hidden");
      mainHeader.classList.add("main-header-visible");

      storeHeader.classList.remove("fade-out-top");
      storeHeader.classList.add("fade-in-top");

      mainHeader.classList.remove("fade-out-top");
      mainHeader.classList.add("fade-in-top");

      transparentHeader.classList.remove("transparent-main-header-visible");
      transparentHeader.classList.add("transparent-main-header-hidden");
    }

    const debouncedPromobarVisibility = debounce(updateHeaderVisibility, 100);

    document.addEventListener("scroll", function () {
      debouncedPromobarVisibility();
    });
  }
  
  initProductTabs();
  homepageHeroBannerMobileFunctionality();
  homepageHeroBannerFixedPositionFunctionality();
  homepageHeroBannerPromobarFunctionality();
  homepageHeroBannerNavFunctionality();

  /**
   * PRODUCT PAGE RELATED CUSTOMIZATIONS ONLY
   */
  if (!pagePath.startsWith("/products/")) {
    return;
  }

  // Handle sample order button visibility on load
  setTimeout(function () {
    updateSampleNotifyMe();
  }, 100);

  function selectFirst(inputListSelector, inputSelector) {
    let inputs = document.querySelectorAll(inputListSelector);
    let hasChecked = false;

    if (inputs.length == 0) {
      return;
    }

    Array.from(inputs).forEach(function (radio) {
      if (radio.checked) {
        hasChecked = true;
      }
    });

    if (!hasChecked) {
      document.querySelector(inputSelector).click();
    }
  }

  // Click first available variant for sample sizes
  const orderSampleButton = document.querySelector("button#SampleRug");
  if (orderSampleButton) {
    // if sample option exists click first available variant swatch on drawer open
    orderSampleButton.addEventListener("click", function () {
      // when user opens Sample Drawer trigger click on first color and size if not already selected
      const colorInputs = ".sample-rug__drawer .color-swatch-list .color-swatch__radio";
      const sizeInputs = ".sample-rug__drawer .block-swatch-list .block-swatch__radio";
      const colorSelector = ".sample-rug__drawer .color-swatch-list .color-swatch__item";
      const sizeSelector = ".sample-rug__drawer .block-swatch-list .block-swatch__item";

      selectFirst(colorInputs, colorSelector);
      selectFirst(sizeInputs, sizeSelector);
    });
  }

  // Get main product
  const mainProduct = await getProduct();

  // Set global variables
  const leadTimePrefix = "Expected to ship in";

  // Sku Update
  document.addEventListener("variant:changed", function (event) {
    let sku = (event.detail.variant["sku"] || "").split("_")[0];
    sku = sku.replaceAll("-", "%20");

    // Spce Sheet link - Update spec sheet url
    let specSheetLink = document.getElementById("spec-sheet-link");
    if (specSheetLink && event.detail.variant["sku"]) {
      let newURL =
        specSheetLink.getAttribute("data-base-url") + sku + "/spec-sheet";
      specSheetLink.setAttribute("href", newURL);
      specSheetLink.classList.remove("hidden");
    } else if (specSheetLink && !event.detail.variant["sku"]) {
      specSheetLink.classList.add("hidden");
    }

    // Hubspot Inquiry Form - update product_inquiry field
    const productInquiryInput = document.querySelector(
      'input[name="product_inquiry"]'
    );
    if (productInquiryInput) {
      productInquiryInput.value = sku;
      productInquiryInput.dispatchEvent(new Event("input", { bubbles: true }));
    }

    // Sale label toggle
    if (window.updatePdpSaleBadge && window.pdpSaleColors && window.pdpSaleColors.length) {
      const color = (event.detail.variant.options[window.pdpColorOptionIndex] || '').toLowerCase();
      window.updatePdpSaleBadge(window.pdpSaleColors.includes(color));
    }
  });

  // Get querystring, then variant ID from parameters, if exists
  // Set the currentVariant if there is a variant ID in the params
  let currentVariant = undefined;
  let variantId = !!qsParams ? qsParams.get("variant") : null;
  Object.keys(mainProduct.variants).forEach((i) => {
    if (parseInt(mainProduct.variants[i].id) === parseInt(variantId)) {
      currentVariant = mainProduct.variants[i];
    }
  });

  // Update Product Details hiding listings without values
  (function () {
    // Get Product Details Element
    const buttonsSelector =
      ".product-tabs > .product-tabs__content_info > " +
      ".product-tabs__tab-item-wrapper > button.collapsible-toggle.heading";
    const productTabsContentButtons =
      document.querySelectorAll(buttonsSelector);
    const productDetailsButton = Array.from(productTabsContentButtons).find(
      (el) => el.textContent.includes("Product Details")
    );
    const productDetails = productDetailsButton.parentElement.querySelector(
      ".product-tabs__tab-item-content.rte"
    );

    // Get rendered Product metafield data elements
    const isTableData = productDetails.getElementsByTagName("table").length > 0;
    const selectTag = isTableData ? "tr" : "li";
    const potentialElementsArray = Array.from(
      productDetails.getElementsByTagName(selectTag)
    );
    const parentElements = potentialElementsArray.filter(
      (el) => el.getElementsByTagName(selectTag).length > 0
    );
    const topLevelRecords = potentialElementsArray.filter((el) => {
      for (const parentElement of parentElements) {
        if (!parentElement.isSameNode(el) && parentElement.contains(el)) {
          return false;
        }
      }
      return true;
    });
    const metafieldElements = isTableData
      ? topLevelRecords.filter((el) => el.getElementsByTagName("th").length < 1)
      : topLevelRecords;
    const parseValueAndLabel = (el) =>
      el.innerText.split(":").map((item) => item.trim()); // TODO expand handling to support table or other potential list metafield formats
    const removeEmptyMetaField = (el) => (el.hidden = true); // Update to delete node if desired

    // Hide empty metafield Elements
    for (const metafieldElement of metafieldElements) {
      const [field, fieldValue] = parseValueAndLabel(metafieldElement);
      const parts = metafieldElement.textContent.split(":");

      // Check if there are at least two parts (text before and after ":")
      if (parts.length >= 2) {
        // The item has text after ":"
        // You can access it using parts[1] (trim() is used to remove any leading/trailing spaces)
        const textAfterColon = parts[1].trim();

        // Check if textAfterColon is not empty
        if (textAfterColon.length > 1) {
          // Do something with the item that has text after ":"
          metafieldElement.removeAttribute("hidden");
        } else {
          metafieldElement.setAttribute("hidden", false);
        }
      }
    }
  })();

  /* Check (reliably) for a request from cart to a custom size variant */
  if (variantId !== null) {
    // Get the variant from the mainProduct data with the ID from the querystring
    let currentVariantName = "";
    Object.keys(mainProduct.variants).forEach((i) => {
      if (parseInt(mainProduct.variants[i].id) === parseInt(variantId)) {
        currentVariantName = mainProduct.variants[i].name;
      }
    });

    if (currentVariantName.includes("Custom - ")) {
      /* Custom size variant logic (onload only) */
      // Redirect to the parent product
      console.log(
        "redirecting from custom variant " +
          currentVariantName +
          " to main PDP: " +
          location.href.split("?")[0]
      );
      location.replace(location.href.split("?")[0]);
    }
  }

  // Show PDP Product SKU without variant variations after "variant:changed"
  const productSkuNumber = document.querySelector(
    ".product__info > product-meta > div > div > " +
      "[data-product-sku-container] > [data-product-sku-number]"
  );
  if (productSkuNumber) {
    const skuObserver = new MutationObserver(() => {
      const remnantSku = productSkuNumber.dataset.remnantSku;
      if (remnantSku) {
        if (productSkuNumber.innerHTML !== remnantSku) {
          skuObserver.disconnect();
          productSkuNumber.innerHTML = remnantSku;
          skuObserver.observe(productSkuNumber, { childList: true });
        }
        return;
      }
      if (!productSkuNumber.innerHTML.includes("_")) {
        return;
      }
      skuObserver.disconnect();
      productSkuNumber.innerHTML = productSkuNumber.innerHTML.split("_").shift();
      skuObserver.observe(productSkuNumber, { childList: true });
    });
    skuObserver.observe(productSkuNumber, { childList: true });
  }

  const productForm = getProductForm(mainProduct);
  if (productForm === null) {
    return;
  }

  // Store "variant:changed" price updates
  const variantPricing = {
    price: null,
    compare_at_price: null,
    setPrice: function (price) {
      this.price = price;
      return this;
    },
    setCompareAtPrice: function (price) {
      this.compare_at_price = price;
      return this;
    },
  };

  const getCustomPricing = (currentVariantPrice) => {
    const productMetaContainer = document.querySelector('product-meta')
    let productType, customStartingPrice;
    let customPrice = 0;

    if(productMetaContainer) {
      productType = productMetaContainer.dataset.productType;
      customStartingPrice = productMetaContainer.dataset.customStartingPrice;
    }

    if(productType === "Broadloom" && customStartingPrice) {
      customPrice = parseFloat(customStartingPrice) * (isB2b ? 100 - b2bDiscount:100);
    } else {
      customPrice = currentVariantPrice || 0;
    }
    
    return customPrice;
  }

  let topY = 0;

  document.addEventListener('scroll', () => { 
    const scrollPos = document.body.getBoundingClientRect();
    topY = Math.abs(scrollPos.y);
  });

  document.addEventListener("variant:changed", (event) => {
    const isSample = event.target.closest('.product-form__buy-buttons')?.dataset.showSamplesOnly === "true";

    const currentScrollPos = Math.abs(document.body.getBoundingClientRect().y);
    // Compare current scroll position with topY
    if (currentScrollPos !== topY) {
      // Scroll back to topY
      window.scrollTo({
        top: topY,
        behavior: 'instant',
      });
    }

    // Update lead time on variant change
    let thisVariant = event.detail?.variant;

    variantPricing.setPrice(getCustomPricing(thisVariant?.price) || 0);
    variantPricing.setCompareAtPrice(thisVariant?.compare_at_price || 0);

    // Update lead time per variant selected
    if (thisVariant !== undefined) {
      // Update lead time for the selected variant
      let leadTimesDataEl = document.querySelector(isSample ? '[data-show-samples-only="true"] .lead-time-data' : ".lead-time-data");   
      let leadTimeDisplayEl = document.querySelector(isSample ? '[data-show-samples-only="true"] .lead-time-label' : ".lead-time-label");
      let leadTimesData =
        leadTimesDataEl !== undefined ? leadTimesDataEl?.innerHTML : "";
      let leadTimes = leadTimesData?.split("|");
      let leadTimeInput = document.querySelector(
        'input[name="properties[_leadTime]"]'
      );

      if(!leadTimes) return;

      Object.keys(leadTimes).forEach((i) => {
        if (thisVariant !== undefined) {
          let variantLeadTime = leadTimes[i].split(":");
          if (parseInt(variantLeadTime[0]) === parseInt(thisVariant.id)) {
            if(leadTimeDisplayEl) {
              if (variantLeadTime[1] === "") {
                leadTimeDisplayEl.innerHTML = "";
                leadTimeInput.setAttribute("value", "");
              } else {
                leadTimeDisplayEl.innerHTML =
                  leadTimePrefix + " " + variantLeadTime[1];
                leadTimeInput.setAttribute("value", variantLeadTime[1]);
              }
            }
          }
        }
      });
    }
  });

  // "Order a Sample" updates on variant change
  document.addEventListener("variant:changed", (event) => {
    event.preventDefault();
    updateVariantData();
  });

  const submitButton = getFormSubmitButton(productForm);
  const hasCustomOption = document.querySelector(".custom-size-selector");
  const hasProductInquiryButton = document.getElementById("product-inquiry-button");
  const currentColorChecked = document.querySelector('.color-swatch__radio[checked=checked][data-initial-selection]')  
  const isB2bEl = document.querySelector(".customer-type-data");
  const isB2b = isB2bEl !== undefined ? isB2bEl.innerHTML === "true" : false;
  const b2bDiscountEl = document.querySelector(".b2b-discount-data");
  const b2bDiscount =
    b2bDiscountEl !== undefined ? parseInt(b2bDiscountEl.innerHTML) : 0;


  function validateSelections() {
    setTimeout(
      function() {
        const currentColor = document.querySelector('.product-form__option-info .product-form__option-value');
        const currentSizeContainer = document.querySelector('.block-swatch-list--size');
        const currentSize = document.querySelector('.block-swatch-list--size .block-swatch__radio[checked=checked]:not([data-initial-selection])');
        const colorSwatches = document.querySelector('.product-form .color-swatch-list')
        const errColor = document.getElementsByClassName("alert color")[0];
        const errSize = document.getElementsByClassName("alert size")[0];

        if (currentColor.innerText.length < 1) {
          errColor.innerText = "Please select a color";
        } else {
          errColor.innerHTML = "";
        }

        if (currentSize == null) {
          errSize.innerText = "Please select a size";
        } else {
          errSize.innerHTML = "";
        }
      }, 0
    );
  }

  validateSelections();

  if(hasProductInquiryButton){
    currentColorChecked.addEventListener('click', async (event) =>{
      if(event.target.hasAttribute('data-initial-selection')){
        const currentColorLabel = document.querySelector('.product-form__option-selector[data-selector-type=color] .product-form__option-value');

        currentColorLabel.innerText = event.target.value;
        await updateVariantData();
        await updateSampleNotifyMe();
      }
    });
  } else if(hasCustomOption !== null){
    if(currentColorChecked){
      currentColorChecked.addEventListener('click', async (event) =>{
        if(event.target.hasAttribute('data-initial-selection')){
          const currentColorLabel = document.querySelector('.product-form__option-selector[data-selector-type=color] .product-form__option-value');

          currentColorLabel.innerText = event.target.value;
          await updateVariantData();
          atcOriginaPriceSpanUpdateCustomFirstVariant();
          validateCustomDimensionsInput();
          await updateSampleNotifyMe();
        }
      });
    }

    function atcOriginaPriceSpanUpdateCustomFirstVariant() { 
      const productMeta = document.querySelector('product-meta');
      const productType = productMeta.dataset.productType;
      const startingPrice = parseFloat(productMeta.dataset.customStartingPrice);  
      const pdpPriceExtract = pdpPriceElement.innerText.match(/\$([0-9]+(?:\.[0-9]+)?)/);
      const currentPrice = pdpPriceExtract ? parseFloat(pdpPriceExtract[1]) * 100 : null;
      let newTotalPrice = getCustomPricing(startingPrice);
      let sizeSelectionLocal = localStorage.getItem("sizeSelection");
      let storedSize = null;

      if(sizeSelectionLocal){
        storedSize = JSON.parse(sizeSelectionLocal);
      }

      if(productType === 'Broadloom' && currentPrice <= newTotalPrice){
        // If no set size is selected, there is no need to update span on first variant click.
        if(storedSize === null || storedSize.total_area_inches === 0) {
          pdpPriceElement.innerHTML = `${formatMoney(newTotalPrice)} USD`;
        }
      }
    }
  } else if (hasCustomOption == null) {
    /**
     * STANDARD SIZE RELATED CUSTOMIZATIONS ONLY
     */
    const pristineInput = document.querySelector(".pristine-treatment__input");
    const pristinePrice = document.querySelector(".pristine-price");
    const rugPadInput = document.querySelector(".rug-pad__input");
    const rugPadPrice = document.querySelector(".rug-pad-price");
    const priceList = document.querySelector("[data-product-price-list]");
    const outerPriceElements = document.querySelectorAll(
      ".price-list > .price"
    );
    const pristineCheck = document.querySelector(
      '[type="checkbox"][name="properties[Add pristine rug treatment]"]'
    );
    const rugPadCheck = document.querySelector(
      '[type="checkbox"][name="properties[Add compatible rug pad]"]'
    );
    const pdpPriceElement = document.querySelector(
      ".price-list span.price.price--large .price-only"
    );
    const atcButton = document.querySelector("#AddToCart .loader-button__text");

    await updateAddOnPrices();
    await atcPriceSpanUpdate();

    pristineCheck.addEventListener("change", () => {
      atcPriceSpanUpdate();
      toggleCompareAndCustomClassHide();
    });
    rugPadCheck.addEventListener("change", () => {
      atcPriceSpanUpdate();
      toggleCompareAndCustomClassHide();
    });

    function getSelectedSize() {
      return document
        .querySelector(".block-swatch__radio:checked+.block-swatch__item")
        .innerHTML.toLowerCase();
    }

    function getSelectedColor() {
      return document
        .querySelector(
          ".color-swatch__radio:checked+.color-swatch__item>.visually-hidden"
        )
        .innerHTML.toLowerCase();
    }

    async function getAddOnProduct(type) {
      const types = {
        rug: "rug-pad",
        pristine: "pristine-treatment",
      };
      if (!types.hasOwnProperty(type)) {
        return false;
      }
      const lineItem = document.querySelector(`.${types[type]}__line-item`);
      if (!lineItem) {
        return false;
      }
      const productPath = lineItem.getAttribute(`data-${type}-product`);
      const response = await fetch(`${productPath}.js`);
      return await getResponseJson("getAddOnProduct", response);
    }

    function getAddOnVariant(product) {
      const thisVar = product["variants"].find((variant) =>
        variant["title"].toLowerCase().includes(getSelectedSize())
      );
      Object.keys(mainProduct.variants).forEach((i) => {
        currentVariantName = mainProduct.variants[i].name;
      });
      return thisVar;
    }

    function getProductVariant(product) {
      const thisVar = product["variants"].find((variant) => {
        return (
          variant["title"].toLowerCase().includes(getSelectedSize()) &&
          variant["title"].toLowerCase().includes(getSelectedColor())
        );
      });
      Object.keys(mainProduct.variants).forEach((i) => {
        currentVariantName = mainProduct.variants[i].name;
      });
      return thisVar;
    }

    async function updateAddOnPrice(product, priceElement) {
      // go through variants
      // find variant whose name includes selected size
      //  - get variant price
      //  - add variant price to base
      //  - add variant price to line item
      const addOnVariant = await getAddOnVariant(product);

      if (!addOnVariant) return;

      const type = priceElement.classList.contains("pristine-price") ? "pristine" : "rug-pad";
      const saleData = getAddOnSaleData(type);

      // When the add-on is on sale, show the sale price (e.g. 0.00 = free) instead of the
      // regular variant price, and stamp the sale description onto the input so the cart
      // line-item property (and its promo badge) picks it up.
      if (saleData?.is_sale) {
        priceElement.innerHTML = (saleData.sale_price ?? 0).toFixed(2);
        const inputSelector = type === "pristine" ? ".pristine-treatment__input" : ".rug-pad__input";
        const addOnInput = document.querySelector(inputSelector);
        if (addOnInput && saleData.sale_description) {
          addOnInput.setAttribute("data-description", saleData.sale_description);
        }
        return;
      }

      const addOnVariantPrice = addOnVariant["price"] / 100;
      priceElement.innerHTML = addOnVariantPrice.toFixed(2);
    }

    async function returnAddOnPrice(product, type) {
      const saleData = type ? getAddOnSaleData(type) : null;
      if (saleData?.is_sale) {
        return (saleData.sale_price ?? 0).toFixed(2);
      }
      const addOnVariant = await getAddOnVariant(product);
      const addOnVariantPrice = addOnVariant["price"] / 100;
      return addOnVariantPrice.toFixed(2);
    }

    async function returnProductPrice(product) {
      const productVariant = await getProductVariant(product);
      const productVariantPrice = productVariant["price"] / 100;
      return productVariantPrice.toFixed(2);
    }

    async function returnProductAvailability(product) {
      const productVariant = await getProductVariant(product);
      const availability   = productVariant["available"];
      return availability;
    }

    async function updateAddOnPrices() {
      const rugProduct = await getAddOnProduct("rug");
      updateAddOnPrice(rugProduct, rugPadPrice);

      const pristineProduct = await getAddOnProduct("pristine");
      if (pristineProduct) {
        updateAddOnPrice(pristineProduct, pristinePrice);
      }
    }

    function atcOriginaPriceSpanUpdate() {
      const originalPriceCompare = document.querySelector('[data-original-price-compare]');
      const priceListCompare = priceList.querySelector('.price.price--compare');
      if(!originalPriceCompare) return;

      const formatOriginalPriceCompare = originalPriceCompare.dataset.originalPriceCompare;
      if(formatOriginalPriceCompare) {
        priceListCompare.innerText = `${formatMoney(formatOriginalPriceCompare)} USD`;
      }
    }

    async function atcPriceSpanUpdate() {
      let productPath = window.location.pathname;
      let response = await fetch(`${productPath}.js`);
      let productJson = await getResponseJson("product", response);
      let prodPrice = await returnProductPrice(productJson);
      let total = parseFloat(prodPrice);
      let prodAvailability = await returnProductAvailability(productJson);
      let isInitialSelection = document.querySelectorAll('[data-initial-selection]');
      let newPriceString = '';
      let atcButtonParent = atcButton.parentElement;

      if (rugPadInput.checked) {
        const rugProduct = await getAddOnProduct("rug");
        let rPrice = await returnAddOnPrice(rugProduct, "rug-pad");
        total += parseFloat(rPrice);
      }

      if (pristineInput.checked) {
        const pristineProduct = await getAddOnProduct("pristine");
        if (pristineProduct) {
          let pPrice = await returnAddOnPrice(pristineProduct, "pristine");
          total += parseFloat(pPrice);
        }
      }

      newPriceString += `${formatMoney(total * 100)} USD`;
      if (isInitialSelection.length) {
        const lowestPricedItem = productJson.variants
        .filter(item => item.available && !item.title.toLowerCase().includes("sample"))
        .sort((a, b) => a.price-b.price) || null;
        atcButtonParent.disabled = true;

        if(prodAvailability){
          if(lowestPricedItem) {
            pdpPriceElement.innerHTML = `Starting at ${formatMoney(lowestPricedItem[0].price)} USD`;
            atcButton.innerHTML = `Add To Cart`;
          } else {
            pdpPriceElement.innerHTML = newPriceString;
            atcButton.innerHTML = `Add To Cart - ${newPriceString}`;
          }
        } else {
          atcButton.innerHTML = `Out of stock - ${newPriceString}`;
        }
      } else {
        if(prodAvailability){
          atcButtonParent.disabled = false;
          pdpPriceElement.innerHTML = newPriceString;
          atcButton.innerHTML = `Add To Cart - ${newPriceString}`;
        } else {
          atcButtonParent.disabled = true;
          atcButton.innerHTML = `Out of stock - ${newPriceString}`;
        }
      }
    }

    async function getCartItemCount() {
      // Get the cart object
      let cart = JSON.parse(localStorage.getItem("cart"));
      return cart !== null ? cart.items.length : 0;
    }

    document.addEventListener("variant:changed", updateAddOnPrices);
    document.addEventListener("variant:changed", () => {
      atcPriceSpanUpdate();
      toggleCompareAndCustomClassHide();
      updateSampleNotifyMe();
    });
    const standardRugPriceListMutationObserver =
      addPostVariantChangedEventListener(
        priceList,
        outerPriceElements,
        priceListIsTargetMutation,
        priceListReAddNode
      );

    if(currentColorChecked){
      currentColorChecked.addEventListener('click', async (event) =>{
        if(event.target.hasAttribute('data-initial-selection')){
          const currentColorLabel = document.querySelector('.product-form__option-selector[data-selector-type=color] .product-form__option-value');
          
          currentColorLabel.innerText = event.target.value;
          await updateVariantData();
          atcOriginaPriceSpanUpdate();
          atcPriceSpanUpdate();
          await updateSampleNotifyMe();
        }
      });
    }
    
    submitButton.addEventListener("click", async function (event) {
      if (event.preventLoop) {
        return;
      }

      const { target } = event;
      const originalEvent = new event.constructor(event.type, event);
      originalEvent.preventLoop = true;
      event.stopImmediatePropagation();
      event.stopPropagation();
      event.preventDefault();

      submitButton.setAttribute("disabled", "disabled");
      toggleFormSubmitButtons(productForm, false);

      const addOns = [];
      const uniqueString = Math.random().toString(16).slice(2);
      await updateAddOnPrices();

      // Prepare selected add ons
      if (pristineInput.checked) {
        const pristineProduct = await getAddOnProduct("pristine");
        const pristineVariant = getAddOnVariant(pristineProduct);
        addOns.push(pristineVariant);
      }
      if (rugPadInput.checked) {
        const rugPadProduct = await getAddOnProduct("rug");
        const rugPadVariant = getAddOnVariant(rugPadProduct);
        addOns.push(rugPadVariant);
      }

      // Add to Cart
      const formData = new FormData(productForm);
      const qtyElement = document.querySelector('[name="quantity"]');
      let qty = 1;
      if (qtyElement !== undefined) {
        qty = qtyElement.value;
      }

      let leadTime = await getLeadTime();
      leadTime = leadTime !== null ? leadTime.replace(leadTimePrefix, "") : "";

      let sortOrder = (await getCartItemCount()) + 1;
      const lineItemProperties = {};
      if (rugPadInput.checked) {
        sortOrder++;
      }
      if (pristineInput.checked) {
        sortOrder++;
      }
      if (leadTime !== "") {
        lineItemProperties["Expected to ship in"] = leadTime;
      }

      // Add sort order and tie add ons together with this product with a line item property
      //  containing a unique identifier to keep like items from grouping in cart
      const cartItemDetails = formData.get("id") + "_" + uniqueString;
      lineItemProperties._cartItemDetails = sortOrder++ + "_" + cartItemDetails;

      const items = [];
      items.push({
        id: formData.get("id"),
        quantity: qty,
        properties: lineItemProperties,
      });

      // May need to fix the sortOrder to be accurate
      //  This is currently the same sortOrder for both add ons
      const addOnLineItemProperties = {};

      const currentVariant = await getActiveVariant(mainProduct, formData);
      addOnLineItemProperties["Add-On for"] = currentVariant.name;
      addOnLineItemProperties._cartItemDetails =
        sortOrder++ + "_" + cartItemDetails;

      addOns.forEach((addOn) =>
        items.push({
          id: addOn.id,
          quantity: qty,
          properties: addOnLineItemProperties,
        })
      );

      const itemData = { items };
      console.log(`itemData:`, itemData);

      const result = fetch(window.Shopify.routes.root + "cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(itemData),
      }).then(function (response) {

        let responseJson = getResponseJson(
          `standard rug addProduct fetch response`,
          response
        );
        // theme.js post-/cart/add dispatch event code
        productForm.dispatchEvent(
          new CustomEvent("variant:added", {
            bubbles: true,
            detail: {
              variant: responseJson.hasOwnProperty("items")
                ? responseJson["items"][0]
                : responseJson,
            },
          })
        );
        rebuildCart(productForm);
        submitButton.removeAttribute("disabled");
        toggleFormSubmitButtons(productForm, true);

        return responseJson;
      });

      return result;
    });

    return;
  }

  /**
   * CUSTOM SIZE RELATED CUSTOMIZATIONS ONLY
   */
  // Get price and dimensions and elements

  function resetRemnantsTab(){ 
    const remnantsMoreInfoInput = document.getElementById('remnant-more-info__input');
    if(!remnantsMoreInfoInput) return;
    remnantsMoreInfoInput.checked = '';
  }

  const pdpPriceElement = document.querySelector(
    ".price-list span.price.price--large .price-only"
  );
  const atcPrice = document.querySelector("#AddToCart span.atc-price");

  function getOrCreateAtcPriceElement() {
    let priceEl = document.querySelector("#AddToCart span.atc-price");
    if (!priceEl) {
      const textWrapper = document.querySelector("#AddToCart .loader-button__text");
      if (!textWrapper) return null;
      priceEl = document.createElement("span");
      priceEl.className = "atc-price";
      textWrapper.appendChild(priceEl);
    }
    return priceEl;
  }

  let lastCalculatedPriceString = null;

  function syncAtcPriceWithPdpPrice() {
    const priceEl = getOrCreateAtcPriceElement();
    if (!priceEl || !lastCalculatedPriceString) return;
    priceEl.innerHTML = " - " + lastCalculatedPriceString;
  }

  const widthFt = document.querySelector('[name="customSizeWidthFt"]');
  const widthIn = document.querySelector('[name="customSizeWidthIn"]');
  const lengthFt = document.querySelector('[name="customSizeLengthFt"]');
  const customMinSqFt = document.querySelector('[name="minTotalSqFt"]');
  const lengthIn = document.querySelector('[name="customSizeLengthIn"]');
  const maxWidthFt = document.querySelector('[name="maxWidthFt"]');
  const maxWidthIn = document
    .querySelector('[name="maxWidthFt"]')
    .getAttribute("inches-max-width");
  const pristineCheck = document.querySelector(
    '[type="checkbox"][name="properties[Add pristine rug treatment]"]'
  );
  const rugPadCheck = document.querySelector(
    '[type="checkbox"][name="properties[Add compatible rug pad]"]'
  );
  const priceList = document.querySelector("[data-product-price-list]");
  const atcButton = document.querySelector("#AddToCart");
  const outerPriceElements = document.querySelectorAll(".price-list > .price");
  const mainProductAlertElement = document.querySelector(
    "div#main > .shopify-section--main-product > section > .container > .alert.banner--error"
  );
  const errWidth = document.getElementsByClassName("alert width")[0];
  const errLength = document.getElementsByClassName("alert length")[0];
  const updateAddonsPriceElementsList = [
    widthFt,
    widthIn,
    lengthFt,
    lengthIn,
    priceList,
    atcButton,
  ];
  const tabs = document.querySelectorAll('.product-tabs__tab');

  let minSqFt = customMinSqFt;

  if(tabs){
    const customSizeWidthFt = document.querySelector('[name="customSizeWidthFt"]');
    const customSizeLengthFt = document.querySelector('[name="customSizeLengthFt"]');
    const quantityInput = document.querySelector('input.quantity-selector__input');
    const remnantDisclaimerInput = document.querySelector(".remnant-disclaimer__input");
    const productFormColors = document.querySelector('.product-form .color-swatch .color-swatch__radio');
    const rugPadInput = document.querySelector('[data-rug-product-pre_checked]');
    const pristineInput = document.querySelector('[data-pristine-product-pre_checked]');
    const defaultMinSqFt = customMinSqFt.value;

    tabs.forEach(tab => {
      tab.addEventListener('click', function(e) {
        const tabType = this.dataset.tab;
        const rugPadPreChecked = rugPadInput?.dataset.rugProductPre_checked === 'true';
        const pristinePreChecked = pristineInput?.dataset.pristineProductPre_checked === 'true';

        const b2bRemnantBtn = document.querySelector('[data-show-b2b-remnant="true"]');
        const b2bRemnantAddToCart = b2bRemnantBtn?.parentElement?.querySelector('#AddToCart');

        if(tabType === 'remnants'){          
          remnantDisclaimerInput.setAttribute("checked", "checked");
          minSqFt.value = 0;
          resetRemnantsTab();
          if(rugPadInput) {
            rugPadInput.checked = false;
          }
          if(pristineInput) {
            pristineInput.checked = false;
          }
          if(b2bRemnantBtn) {
            b2bRemnantBtn.classList.add('visually-hidden');
            b2bRemnantAddToCart?.classList.remove('visually-hidden');
          }
          const productInquiryBtn = document.querySelector('#product-inquiry-button');
          if(productInquiryBtn?.parentElement) {
            productInquiryBtn.parentElement.classList.add('visually-hidden');
          }
        } else {
          remnantDisclaimerInput.setAttribute("checked", "");
          minSqFt.value = defaultMinSqFt;
          customSizeWidthFt.value = '';
          customSizeLengthFt.value = '';
          productFormColors.click();
          if(rugPadInput) {
            rugPadInput.checked = rugPadPreChecked;
          }
          if(pristineInput) {
            pristineInput.checked = pristinePreChecked;
          }
          if(b2bRemnantBtn) {
            b2bRemnantBtn.classList.remove('visually-hidden');
            b2bRemnantAddToCart?.classList.add('visually-hidden');
          }
          const productInquiryBtn = document.querySelector('#product-inquiry-button');
          if(productInquiryBtn?.parentElement) {
            productInquiryBtn.parentElement.classList.remove('visually-hidden');
          }
        }

        quantityInput.value = 1;
      });
    });
  }


  const debouncedUpdateCustomPricesListener = debounce(
    updateCustomPricesListener,
    400
  );

  addDimensionChangeEventListener(widthFt);
  addDimensionChangeEventListener(widthIn);
  addDimensionChangeEventListener(lengthFt);
  addDimensionChangeEventListener(lengthIn);
  pristineCheck.addEventListener("change", updateCustomPricesListener);
  rugPadCheck.addEventListener("change", updateCustomPricesListener);
  if (widthFt) {
    widthFt.addEventListener("change", limitDimensionInchOptions);
  }
  if (lengthFt) {
    lengthFt.addEventListener("change", limitDimensionInchOptions);
  }
  inputSavedDimensions();
  limitDimensionInchOptions();

  function inputSavedDimensions() {
    let sizeSelectionLocal = localStorage.getItem("sizeSelection");
    if (!sizeSelectionLocal) {
      return;
    }
    let storedSize = JSON.parse(sizeSelectionLocal);

    let maxWidthInches = parseInt(maxWidthIn);
    let minAreaInches = parseFloat(minSqFt.value) * 144;

    let selectedWidthTotalInches = storedSize['total_width_inches'];
    if (!Number.isNaN(maxWidthInches) && selectedWidthTotalInches > maxWidthInches) {
      selectedWidthTotalInches = maxWidthInches;
    }

    let selectedLengthTotalInches = storedSize['total_length_inches'];
    let selectedArea = selectedWidthTotalInches * selectedLengthTotalInches;
    if (!Number.isNaN(minAreaInches) && selectedArea < minAreaInches) {
      selectedLengthTotalInches = Math.ceil(minAreaInches / selectedWidthTotalInches);
    }

    widthFt.value = Math.floor(selectedWidthTotalInches / 12);
    widthIn.value = selectedWidthTotalInches % 12;
    lengthFt.value = Math.floor(selectedLengthTotalInches / 12);
    lengthIn.value = selectedLengthTotalInches % 12;

    lengthIn.dispatchEvent(new Event('input'));
  }

  // Run updateCustomPricesListener post-variants:changed
  const priceListMutationObserver = addPostVariantChangedEventListener(
    priceList,
    outerPriceElements,
    priceListIsTargetMutation,
    priceListReAddNode,
    updateCustomPricesListener
  );
  const atcPriceMutationObserver = addPostVariantChangedEventListener(
    atcPrice.parentElement,
    atcPrice,
    (mutation, preservedElements) => {
      if (mutation.target.contains(preservedElements)) {
        return false;
      }
      for (const removedNode of mutation.removedNodes.values()) {
        if (
          removedNode
            .cloneNode(false)
            .isEqualNode(preservedElements.cloneNode(false))
        ) {
          return removedNode;
        }
      }
      return false;
    },
    (mutation, removedNodes) => mutation.target.appendChild(removedNodes),
    updateCustomPricesListener
  );

  setDefaultAddOnPrice("pristine", productForm);
  setDefaultAddOnPrice("rug-pad", productForm);

  /** Event listeners **/
  async function addDimensionChangeEventListener(el) {
    el.addEventListener("input", debouncedUpdateCustomPricesListener);
  }

  function addPostVariantChangedEventListener(
    observedElement,
    preservedElements,
    isTargetMutation,
    reAddNodes,
    dispatchEventCallback = () => null,
    config = { childList: true }
  ) {
    const eventName = "variant:change-custom-price";
    const observer = new MutationObserver((mutationList, observer) => {
      toggleCompareAndCustomClassHide(false);

      let shouldDispatch = false;
      let targetMutation, removedNodes;
      for (const mutation of mutationList) {
        removedNodes = isTargetMutation(mutation, preservedElements);
        if (removedNodes) {
          targetMutation = mutation;
          break;
        }
      }
      if (removedNodes) {
        shouldDispatch = reAddNodes(targetMutation, removedNodes);
      }
      if (shouldDispatch) {
        const variantChangeCustomPrice = new Event(eventName);
        observedElement.dispatchEvent(variantChangeCustomPrice);
      }
    });
    observer.observe(observedElement, config);
    observedElement.addEventListener(eventName, dispatchEventCallback);
    return observer;
  }

  function priceListIsTargetMutation(mutation, preservedElements) {
    if (
      Array.from(preservedElements.values()).includes((el) =>
        mutation.target.contains(el)
      )
    ) {
      return false;
    }
    if (mutation.removedNodes.length > 0) {
      return mutation.removedNodes;
    }
    return false;
  }

  function priceListReAddNode(mutation, removedNodes) {
    const currencyFormat = window.themeVariables.settings.currencyCodeEnabled
      ? window.themeVariables.settings.moneyWithCurrencyFormat
      : window.themeVariables.settings.moneyFormat;
    mutation.target.innerHTML = "";
    removedNodes.forEach((node) => {
      if (!(node instanceof Element)) {
        return;
      }
      const priceOnlySpan = node.querySelector(".price-only");
      if (priceOnlySpan) {
        const updatedVariantPricing = getCustomPricing(variantPricing.price);
        mutation.target.append(node);
        return;
      }
      const priceCompareSpan = node.querySelector("span.price.price--compare");
      if (!priceCompareSpan) {
        return;
      }
      const priceCompareTextNode = Array.from(
        priceCompareSpan.childNodes.values()
      ).find(
        (value) =>
          value.nodeName === "#text" &&
          value.nodeValue.match(/\$[\d,]+\.\d{2}/) !== null
      );
      priceCompareTextNode.nodeValue = `${formatMoney(
        variantPricing.compare_at_price,
        currencyFormat
      )} `;
      mutation.target.append(node);
      return;
    });
    return true;
  }

  function removeRemnantsSelectInCart(cartContent) {
    const remnantLineItems = cartContent.items.filter(item => item.properties._isRemnant);

    if(remnantLineItems.length === 0){
      return;
    }
    
    const remnantsSelectionBox = document.getElementById('remnant-selection-combo-box');
    const remnantsSelectionBoxOptions = remnantsSelectionBox.querySelectorAll('.combo-box__option-item');
    const remnantSelection = document.getElementById('remnant-selection-value');
    const remnantDefaultLabelValue = remnantSelection.dataset.remnantDefaultLabel;
    const remnantAllItemsInCartValue = remnantSelection.dataset.remnantAllItemsInCartLabel;
    const addToCartButtonElement = document.querySelector('#AddToCart[data-show-samples-only=false]');
    const addToCartText = addToCartButtonElement.dataset.buttonContent;
    
    remnantLineItems.forEach(item => {
      const remnantLabel = item.properties._remnantSelectLabel;

      // Remove all options where text matches remnantLabel
      remnantsSelectionBoxOptions.forEach(option => {
        if (option.textContent.trim() === remnantLabel.trim()) { 
          option.disabled = true;
          addToCartButtonElement.disabled = true;
        } 
      });

      remnantSelection.textContent = remnantDefaultLabelValue;
    });

    const hiddenOptionsCount = Array.from(remnantsSelectionBoxOptions).filter(option => option.hasAttribute('disabled')).length;

    if (hiddenOptionsCount === remnantsSelectionBoxOptions.length) {
      remnantSelection.textContent = remnantAllItemsInCartValue;
    }
  }

  function clearAtcPriceDisplay() {
    const freshAtcPrice = document.querySelector('#AddToCart .loader-button__text span.atc-price')
                       || document.querySelector('#AddToCart span.atc-price');
    if (freshAtcPrice) freshAtcPrice.innerHTML = '';
  }

  submitButton.addEventListener("click", async function (event) {
    if (event.preventLoop) {
      return;
    }

    const { target } = event;
    const originalEvent = new event.constructor(event.type, event);
    originalEvent.preventLoop = true;
    event.stopImmediatePropagation();
    event.stopPropagation();
    event.preventDefault();

    /** Custom Size Variant ProductForm workflow **/
    const isCustomSize = getIsCustomSize();
    console.log("isCustomSize=" + isCustomSize);
    if (!isCustomSize) {
      target.dispatchEvent(originalEvent);
      return false;
    }

    try {
      submitButton.setAttribute("disabled", "disabled");
      submitButton.setAttribute("aria-busy", "true");
      toggleFormSubmitButtons(productForm, false);

      const isRemnantSubmission = !!document.querySelector(".remnant-disclaimer__input")?.checked;

      if (isRemnantSubmission) {
        atcButton.setAttribute('disabled', 'disabled');
        clearAtcPriceDisplay();
      }

      const variantId = await getCustomSizeVariant(productForm);
      console.log(`getCustomSizeVariant() returned variantId: ${variantId}`);
      if (!variantId) {
        throw new CustomRugApiError(
          window.themeVariables.strings.errorCustomRugApi,
          { cause: { code: "NoVariantId" } }
        );
      }

      if (isRemnantSubmission) {
        clearAtcPriceDisplay();
      }

      await setTimeout(async () => {
        const responseJson = await addProduct(variantId, productForm); // [ATC-2] custom size path

        removeRemnantsSelectInCart(responseJson);
        resetRemnantsTab();

        submitButton.removeAttribute("disabled");
        submitButton.removeAttribute("aria-busy");

        // The piece just added is no longer available to re-select, so lock the button until a new one is chosen
        const addedRemnantItem = responseJson?.items?.find((item) => item.properties?._isRemnant);
        if (addedRemnantItem || isRemnantSubmission) {
          atcButton.setAttribute('disabled', 'disabled');
          clearAtcPriceDisplay();
          if (pdpPriceElement) pdpPriceElement.innerHTML = '';
          lastCalculatedPriceString = null;

          const skuEl = document.querySelector('[data-product-sku-number]');
          if (skuEl) {
            delete skuEl.dataset.remnantSku;
            delete skuEl.dataset.remnantPrice;
            skuEl.textContent = '';
          }
        }
      }, 600);
    } catch (e) {
      console.log(e.message);

      submitButton.removeAttribute("disabled");
      submitButton.removeAttribute("aria-busy");
      toggleFormSubmitButtons(productForm, false, false);
      if (e instanceof PriceApiError) {
        displayMainProductError(e.message);
      } else if (e instanceof CustomRugApiError) {
        displayMainProductError(e.message);
      } else {
        displayMainProductError(
          window.themeVariables.strings.errorProductGeneral
        );
      }
      return;
    }
  });

  /** Price-related functions **/
  // js/helper/currency.js from theme.js for consistent configured values
  function formatMoney(cents, format = "") {
    if (typeof cents === "string") {
      cents = cents.replace(".", "");
    }
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/,
      formatString = format || window.themeVariables.settings.moneyFormat;
    function defaultTo(value2, defaultValue) {
      return value2 == null || value2 !== value2 ? defaultValue : value2;
    }
    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultTo(precision, 2);
      thousands = defaultTo(thousands, ",");
      decimal = defaultTo(decimal, ".");
      if (isNaN(number) || number == null) {
        return 0;
      }
      number = (number / 100).toFixed(precision);
      let parts = number.split("."),
        dollarsAmount = parts[0].replace(
          /(\d)(?=(\d\d\d)+(?!\d))/g,
          "$1" + thousands
        ),
        centsAmount = parts[1] ? decimal + parts[1] : "";
      return dollarsAmount + centsAmount;
    }
    let value = "";
    switch (formatString.match(placeholderRegex)[1]) {
      case "amount":
        value = formatWithDelimiters(cents, 2);
        break;
      case "amount_no_decimals":
        value = formatWithDelimiters(cents, 0);
        break;
      case "amount_with_space_separator":
        value = formatWithDelimiters(cents, 2, " ", ".");
        break;
      case "amount_with_comma_separator":
        value = formatWithDelimiters(cents, 2, ".", ",");
        break;
      case "amount_with_apostrophe_separator":
        value = formatWithDelimiters(cents, 2, "'", ".");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = formatWithDelimiters(cents, 0, ".", ",");
        break;
      case "amount_no_decimals_with_space_separator":
        value = formatWithDelimiters(cents, 0, " ");
        break;
      case "amount_no_decimals_with_apostrophe_separator":
        value = formatWithDelimiters(cents, 0, "'");
        break;
    }
    if (formatString.indexOf("with_comma_separator") !== -1) {
      return formatString.replace(placeholderRegex, value);
    } else {
      return formatString.replace(placeholderRegex, value);
    }
  }

  function formatMoneyInDollars(dollars) {
    return formatMoney(dollars * 100, "");
  }

  // Read the shop-level add-on sale metafield (mirrored into a hidden input by the
  // pristine-treatment / rug-pad snippets). Returns the parsed object or null.
  // On non-live themes (dev/preview), uses is_sale_development instead of is_sale.
  function getAddOnSaleData(type) {
    const isNonLiveTheme = window.Shopify?.theme?.role !== 'main';
    const saleInputName = type === "pristine" ? "pristine_sale_data" : "rug_pad_sale_data";
    try {
      const saleEl = document.querySelector(`[name="${saleInputName}"]`);
      if (saleEl && saleEl.value) {
        const data = JSON.parse(saleEl.value);
        if (isNonLiveTheme && data) {
          return { ...data, is_sale: data.is_sale_development ?? data.is_sale };
        }
        return data;
      }
    } catch (_) {}
    return null;
  }

  async function setDefaultAddOnPrice(type, productForm) {
    const addOnPriceEl = document.querySelector('[class="' + type + '-price"]');
    const saleData = getAddOnSaleData(type);

    if (saleData?.is_sale) {
      addOnPriceEl.innerHTML = formatMoney((saleData.sale_price ?? 0) * 100) + " per sq. ft.";
      const inputClass = type === "pristine" ? ".pristine-treatment__input" : ".rug-pad__input";
      const addOnInput = document.querySelector(inputClass);
      if (addOnInput && saleData.sale_description) {
        addOnInput.setAttribute("data-description", saleData.sale_description);
      }
    } else {
      const productFormData = new FormData(productForm);
      const currentVariant = await getActiveVariant(mainProduct, productFormData);
      const addOnBasePrice = await getVariantBaseAddOnPrice(currentVariant, type);
      addOnPriceEl.innerHTML = addOnBasePrice + " per sq. ft.";
    }
  }

  async function getCurrentVariantAddOnPrice(type) {
    const addOnPriceEl = document.querySelector('[class="' + type + '-price"]');
    return parseFloat(
      addOnPriceEl.innerHTML
        .replace("$", "")
        .replace(" per sq. ft.", "")
        .replace(" USD", "")
        .replace(",", "") // remove comma for thousands separator
    );
  }

  async function setCurrentVariantAddOnPrice(type, value) {
    const addOnPriceEl = document.querySelector('[class="' + type + '-price"]');
    addOnPriceEl.innerHTML = formatMoney(value * 100);
  }

  async function getVariantBaseAddOnPrice(variant, type) {
    const addOnBasePriceEl = document.querySelector(
      '[name="' + variant.id + "_" + type + '_base_price"]'
    );
    return formatMoney(addOnBasePriceEl.value);
  }

  async function getCurrentAddOnPrices() {
    const pristinePrice = await getCurrentVariantAddOnPrice("pristine");
    const rugPadPrice = await getCurrentVariantAddOnPrice("rug-pad");
    return {
      pristinePrice,
      rugPadPrice,
      totalPrice: pristinePrice + rugPadPrice,
    };
  }

  function toggleCompareAndCustomClassHide(hide = true) {
    const hideClass = "visually-hidden";
    const priceListContainer = document.querySelector(
      ".product__info > product-meta > div .product-meta__price-list-container"
    );
    const nonApplicableToCustomSizePricingSelectors = [
      ".price-list .price > .price-custom-prefix",
      ".price-list .price > .price-custom-postfix",
      ".price-list .price > span.price.price--compare",
      ".price-list .price > .price-custom-prefix.price--compare",
      "[data-product-label-list]",
    ];
    for (const selector of nonApplicableToCustomSizePricingSelectors) {
      const priceElementToHide = priceListContainer.querySelector(selector);
      if (!priceElementToHide?.classList) {
        continue;
      }
      priceElementToHide.classList.toggle(hideClass, hide);
    }
  }

  async function updateCustomPrices(rugPrice, pristinePrice, paddingPrice) {
    let pristinePriceDollars = pristinePrice * 100;
    let paddingPriceDollars = paddingPrice * 100;
    let rugPriceDollars = rugPrice * 100;
    let newPrice = pristinePriceDollars + paddingPriceDollars + rugPriceDollars;

    let newPriceString = `${formatMoney(newPrice)} USD`;
    lastCalculatedPriceString = newPriceString;
    pdpPriceElement.innerHTML = newPriceString;
    const priceEl = getOrCreateAtcPriceElement();
    if (priceEl) {
      priceEl.innerHTML = " - " + newPriceString;
    }

    // Remove initial pricing
    toggleCompareAndCustomClassHide();

    if (pristinePrice !== 0) {
      setCurrentVariantAddOnPrice("pristine", pristinePrice);
    }

    if (paddingPrice !== 0) {
      setCurrentVariantAddOnPrice("rug-pad", paddingPrice);
    }
  }

  async function updateCustomPricesListener(event) {
    const remnantsTabActive = document.getElementById('tab-remnants');
    if (remnantsTabActive && remnantsTabActive.getAttribute('aria-selected') === 'true') {
      const selectedRemnant = document.querySelector('#remnant-selection-combo-box .combo-box__option-item[aria-selected="true"]');
      if (selectedRemnant && selectedRemnant.dataset.itemPrice) {
        updateCustomPrices(parseFloat(selectedRemnant.dataset.itemPrice), 0, 0);
      }
      return;
    }

    let isValidDimensions = validateCustomDimensionsInput();
    let currentColorways = document.querySelector(
      '.product-form__option-selector[data-selector-type="color"] .product-form__option-value'
    ).innerText;
    if (!isValidDimensions) {
      return;
    }

    atcButton.setAttribute("aria-busy", "true");
    try {
      // Call getProductFormData() runs getRugCalculationPricing() updates prices
      await getProductFormData(productForm);
      const { pristinePrice, rugPadPrice } = await getCurrentAddOnPrices();

      toggleFormSubmitButtons(productForm, currentColorways.length && isCurrentVariantAvailable(), false);

      if (!updateAddonsPriceElementsList.includes(event.target)) {
        return;
      }

      if (pristinePrice === 0) {
        const pristineInput = document.querySelector(".pristine-treatment__input");
        if (!pristineInput?.hasAttribute("data-description")) {
          setDefaultAddOnPrice("pristine", productForm);
        }
      }
      if (rugPadPrice === 0) {
        const rugPadInput = document.querySelector(".rug-pad__input");
        if (!rugPadInput?.hasAttribute("data-description")) {
          setDefaultAddOnPrice("rug-pad", productForm);
        }
      }
    } catch (e) {
      console.log(e.message);

      toggleFormSubmitButtons(productForm, currentColorways.length && isCurrentVariantAvailable(), false);

      if (e instanceof PriceApiError) {
        displayMainProductError(e.message);
      } else {
        displayMainProductError(
          window.themeVariables.strings.errorProductGeneral
        );
      }
    } finally {
      atcButton.removeAttribute("aria-busy");
    }
  }

  Number.prototype.round = function (places) {
    return +(Math.round(this + "e+" + places) + "e-" + places);
  };

  /** Dimensions-related functions **/
  function getDimensions(formData) {
    let isValidDimensions = validateCustomDimensionsInput();
    if (!isValidDimensions) {
      errLength.innerHTML = "Cannot get dimensions when sizes are not set";
    }
    const customSizeWidthFt = formData.get("customSizeWidthFt");
    const customSizeWidthIn = formData.get("customSizeWidthIn");
    const customSizeLengthFt = formData.get("customSizeLengthFt");
    const customSizeLengthIn = formData.get("customSizeLengthIn");
    const length = getTotalInches(customSizeLengthFt, customSizeLengthIn);
    const width = getTotalInches(customSizeWidthFt, customSizeWidthIn);

    return { length, width };
  }

  function getTotalInches(feet, inches) {
    const feetInInchesNumber = (Number(feet) || 0) * 12;
    if (feetInInchesNumber < 12) {
      errLength.innerHTML =
        "Rugs are expected to have dimensions with a minimum of 1 foot.";
    }

    const inchesNumber = Number(inches);
    if (inchesNumber < 1) {
      return feetInInchesNumber.toString();
    }

    const totalInches = feetInInchesNumber + inchesNumber;

    return totalInches.toString();
  }

  function dimensionValue(field) {
    if (!field || field.value === "") {
      return NaN;
    }
    return Number(field.value);
  }

  function limitInchOptions(feetField, inchField) {
    if (!feetField || !inchField) {
      return;
    }
    const maxInches = parseInt(feetField.dataset.maxTotalInches, 10);
    if (Number.isNaN(maxInches)) {
      return;
    }
    const feet = dimensionValue(feetField);
    const feetInInches = Number.isNaN(feet) ? 0 : feet * 12;

    Array.from(inchField.options).forEach((option) => {
      if (option.value === "") {
        option.disabled = false;
        return;
      }
      option.disabled = feetInInches + Number(option.value) > maxInches;
    });

    if (inchField.selectedIndex > -1 && inchField.options[inchField.selectedIndex].disabled) {
      inchField.value = "";
    }
  }

  function limitDimensionInchOptions() {
    limitInchOptions(widthFt, widthIn);
    limitInchOptions(lengthFt, lengthIn);
  }

  function markDimensionInvalid(fields, isInvalid) {
    fields.forEach((field) => {
      if (!field) {
        return;
      }
      if (isInvalid) {
        field.setAttribute("aria-invalid", "true");
      } else {
        field.removeAttribute("aria-invalid");
      }
    });
  }

  function validateCustomDimensionsInput() {
    const ftWide = dimensionValue(widthFt),
      inWide = dimensionValue(widthIn),
      ftLong = dimensionValue(lengthFt),
      inLong = dimensionValue(lengthIn),
      validateCondition = () =>
        !isNaN(ftWide) && ftWide > 0 && !isNaN(ftLong) && ftLong > 0;

    let currentColorways = document.querySelector(
      '.product-form__option-selector[data-selector-type="color"] .product-form__option-value'
    ).innerText;

    validateSelections();

    let totalWidthIn = 0;
    if (ftWide) {
      totalWidthIn += ftWide * 12;
    }
    if (inWide) {
      totalWidthIn += inWide;
    }

    let totalLengthIn = 0;
    if (ftLong) {
      totalLengthIn += ftLong * 12;
    }
    if (inLong) {
      totalLengthIn += inLong;
    }
    const totalAreaIn = totalWidthIn * totalLengthIn;

    if (!minSqFt.value || !maxWidthFt) {
      return validateCondition();
    }

    if (totalAreaIn < minSqFt.value * 144) {
      errLength.innerHTML =
        "Minimum total area is " + minSqFt.value + " sq. feet";
        atcButton.setAttribute("disabled", "");
      if (!lengthFt.classList.contains("error-border")) {
        lengthFt.classList.add("error-border");
      }
      if (!lengthIn.classList.contains("error-border")) {
        lengthIn.classList.add("error-border");
      }
      markDimensionInvalid([lengthFt, lengthIn], true);
    } else {
      errLength.innerHTML = "";
      lengthFt.classList.remove("error-border");
      lengthIn.classList.remove("error-border");
      markDimensionInvalid([lengthFt, lengthIn], false);
    }

    if (totalWidthIn <= 0) {
      errWidth.innerHTML = "Please enter a valid width";
    } else if (totalWidthIn > maxWidthIn) {
      errWidth.innerHTML = "Maximum width is " + maxWidthFt.value + " feet";
    } else {
      errWidth.innerHTML = "";
    }

    if (totalWidthIn <= 0 || totalWidthIn > maxWidthIn) {
      if (!widthFt.classList.contains("error-border")) {
        widthFt.classList.add("error-border");
      }
      if (!widthIn.classList.contains("error-border")) {
        widthIn.classList.add("error-border");
      }
      markDimensionInvalid([widthFt, widthIn], true);
    } else {
      widthFt.classList.remove("error-border");
      widthIn.classList.remove("error-border");
      markDimensionInvalid([widthFt, widthIn], false);
    }

    const hasWidthInput = !isNaN(ftWide) || !isNaN(inWide);
    const hasLengthInput = !isNaN(ftLong) || !isNaN(inLong);

    if (
      currentColorways.length !== 0 &&
      errWidth.innerHTML.length === 0 &&
      errLength.innerHTML.length === 0 &&
      hasWidthInput &&
      hasLengthInput &&
      isCurrentVariantAvailable()
    ) {
      if (!atcButton.hasAttribute('aria-busy')) {
        atcButton.removeAttribute("disabled");
      }
    } else {
      atcButton.setAttribute("disabled", "");
    }
    syncAtcPriceWithPdpPrice();

    if (totalAreaIn < minSqFt.value * 144) {
      return false;
    }
    return validateCondition();
  }

  /** Mini cart functions **/
  async function rebuildCart(productForm) {
    fetch(`${window.themeVariables.routes.cartUrl}.js`).then(
      async (response2) => {
        const cartContent = await response2.json();

        productForm.dispatchEvent(
          new CustomEvent("cart:updated", {
            bubbles: true,
            detail: {
              cart: cartContent,
            },
          })
        );

        productForm.dispatchEvent(
          new CustomEvent("cart:refresh", {
            bubbles: true,
            detail: {
              cart: cartContent,
              openMiniCart: productForm.closest(".drawer") === null,
            },
          })
        );
      }
    );
  }

  /** Custom app related functions **/
  function getIsCustomSize() {
    const customSizeLengthFt = document.querySelector(
      '[name="customSizeLengthFt"]'
    );
    return !(customSizeLengthFt === undefined || customSizeLengthFt === null);
  }

  // Custom Size Variant App endpoint requests
  async function getCustomSizeVariant(productForm) {
    const formData = await getProductFormData(productForm);

    if (!formData) {
      return formData;
    }

    let formDataEntries = "";
    for (const entry of formData.entries()) {
      formDataEntries +=
        (formDataEntries.length > 0 ? "," : "") +
        `"${entry[0]}": "${entry[1]}"`;
    }

    // Check for existing variant to avoid creating duplicates
    try {
      const response = await requestXhr(
        `/apps/stark-carpet/get-custom-size-variant`,
        formData
      );

      return await getResponseJson("Get Custom Size Variant", response);
    } catch (e) {
      throw new CustomRugApiError(
        window.themeVariables.strings.errorCustomRugApi,
        { cause: e }
      );
    }
  }

  /** Add to cart functions **/
  async function addProduct(variantId, productForm) {
    // Prevent /cart/add POST immediately following variant create without all Product Variant data available
    const qtyElement = document.querySelector('[name="quantity"]');

    let responseJson = null;
    let qty = 1;
    if (qtyElement !== undefined) {
      qty = qtyElement.value;
    }

    const remnantDisclaimerInput = document.querySelector(".remnant-disclaimer__input");
    const remnantMoreInfoInput = document.getElementById('remnant-more-info__input');

    const pristinePrice = await getCurrentVariantAddOnPrice("pristine");
    const rugPadPrice = await getCurrentVariantAddOnPrice("rug-pad");
    const leadTimePrefix = "Expected to ship in";
    let leadTime = await getLeadTime();

    const pristineInput = document.querySelector(".pristine-treatment__input");
    const pristineDescription = pristineInput?.getAttribute("data-description");
    const rugPadInput = document.querySelector(".rug-pad__input");
    const rugPadDescription = rugPadInput?.getAttribute("data-description");

    let pristinePriceString = formatMoney(pristinePrice * 100).toString();
    if (pristineDescription) pristinePriceString += ` - ${pristineDescription}`;

    let rugPadPriceString = formatMoney(rugPadPrice * 100).toString();
    if (rugPadDescription) rugPadPriceString += ` - ${rugPadDescription}`;

    leadTime = leadTime !== null ? leadTime.replace(leadTimePrefix, "") : "";

    // Would be better to set the properties needed on the lineItemProperties object
    // than to set them, then delete them
    let lineItemProperties = {
      "+ Pristine Treatment": pristinePriceString,
      "+ Rug Pad": rugPadPriceString,
      "Expected to ship in": leadTime,
    };

    if (!pristineCheck.checked) {
      delete lineItemProperties["+ Pristine Treatment"];
    }
    if (!rugPadCheck.checked) {
      delete lineItemProperties["+ Rug Pad"];
    }
    if (leadTime === "") {
      delete lineItemProperties["Expected to ship in"];
    }
    
    if(remnantDisclaimerInput?.checked){
      const selectedRemnant = document.querySelector('#remnant-selection-combo-box .combo-box__option-item[aria-selected="true"]')
        || document.querySelector('#remnant-selection-combo-box select option:checked');
      const remnantDisclaimerLabel = remnantDisclaimerInput.dataset.remnantDisclaimerLabel;
      const remnantDisclaimerText = remnantDisclaimerInput.dataset.remnantDisclaimerText;
      const skuEl = document.querySelector('[data-product-sku-number]');
      const remnantPrice = skuEl?.dataset.remnantPrice;
      const remnantSku = skuEl?.dataset.remnantSku;

      lineItemProperties = {
        "_isRemnant": true,
        "_remnantSelectLabel": selectedRemnant ? selectedRemnant.value : '',
        [`${remnantDisclaimerLabel}`]: remnantDisclaimerText,
        "_requestingMoreRemnantInfo": remnantMoreInfoInput.checked ? true : false,
        ...(remnantPrice ? { "_remnantPrice": remnantPrice } : {}),
        ...(remnantSku ? { "_remnantSku": remnantSku } : {}),
        ...lineItemProperties,
      }
    }

    const itemData = {
      items: [
        {
          id: variantId,
          quantity: qty,
          properties: lineItemProperties,
        },
      ],
    };

    // Add product or custom variant to cart
    responseJson = fetch(window.Shopify.routes.root + "cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(itemData),
    }).then(function (response) {
      responseJson = getResponseJson("addProduct fetch response", response);
      // theme.js post-/cart/add dispatch event code
      productForm.dispatchEvent(
        new CustomEvent("variant:added", {
          bubbles: true,
          detail: {
            variant: responseJson.hasOwnProperty("items")
              ? responseJson["items"][0]
              : responseJson,
          },
        })
      );

      rebuildCart(productForm);
      toggleFormSubmitButtons(productForm, true);

      return responseJson;
    });

    // return original Add to Cart response after theme.js style post-add dispatch
    return responseJson;
  }

  function toggleFormSubmitButtons(
    productForm,
    enable = true,
    ariaBusy = true
  ) {
    const submitButtons =
      productForm.submitButtons ||
      Array.from(productForm.elements).filter(
        (button) => button.type === "submit"
      );

    submitButtons.forEach((submitButton) => {
      if (enable) {
        submitButton.removeAttribute("disabled");
        submitButton.removeAttribute("aria-busy");
      } else {
        submitButton.setAttribute("disabled", "disabled");
        submitButton.setAttribute("aria-busy", ariaBusy);
      }
    });
  }

  /** Form (and other) utility functions **/
  async function getProductFormData(formElement) {
    // Get form data & determine if custom variant app needed
    const productFormData = new FormData(formElement);
    const hasPristine = getPropertiesBool(
      productFormData,
      "Add pristine rug treatment"
    );
    const hasPad = getPropertiesBool(productFormData, "Add compatible rug pad");
    const { length, width } = getDimensions(productFormData);
    const color = getSelectedColor();
    const finishing = getSelectedFinishingMethod();
    const originalSize = getOriginalSize();

    // Update form data and make request
    const listenerMainProduct = await (mainProduct !== undefined
      ? mainProduct
      : getProduct());
    const activeVariant = getActiveVariant(
      listenerMainProduct,
      productFormData
    );

    // Get Translations
    const productMeta = document.querySelector('product-meta');
    let productTranslations = null;
    
    if(productMeta){
      productTranslations = JSON.parse(productMeta.dataset.productTranslations);
    }

    // Remove extra form data
    productFormData.delete("customSizeWidthFt");
    productFormData.delete("customSizeWidthIn");
    productFormData.delete("customSizeLengthFt");
    productFormData.delete("customSizeLengthIn");
    productFormData.delete("option1");
    productFormData.delete("option2");
    productFormData.delete("option3");

    // Update data as needed to create custom variant
    const {
      rugPrice: customPrice,
      pristinePrice: pristinePrice,
      paddingPrice: paddingPrice,
    } = await getRugCalculationPricing(activeVariant, formElement, productMeta ? productTranslations.calculating_price : 'Calculating price...', listenerMainProduct.title);
    const addOnsPricing = await getCurrentAddOnPrices();
    let customVariantPrice = parseFloat(
      customPrice + pristinePrice + paddingPrice
    );
    if (isB2b) {
      let b2bMultiplier = 1 / ((100 - b2bDiscount) / 100);
      customVariantPrice = customVariantPrice * b2bMultiplier;
    }

    // Custom Variant create required fields not in form
    productFormData.append("productId", listenerMainProduct.id);
    productFormData.append("title", listenerMainProduct.title);
    productFormData.append("price", customVariantPrice);
    productFormData.append("sku", activeVariant.sku);
    productFormData.append("color", color);
    productFormData.append("binding", finishing);
    productFormData.append("size", originalSize);
    productFormData.append("handle", listenerMainProduct.handle);
    productFormData.append("length", length);
    productFormData.append("width", width);
    productFormData.append(
      "imageId",
      activeVariant.featured_image !== null
        ? activeVariant.featured_image.id
        : 0
    );
    productFormData.append("hasPristine", hasPristine);
    productFormData.append("hasPad", hasPad);
    productFormData.append(
      "+ Pristine Treatment",
      formatMoney(addOnsPricing.pristinePrice).toString()
    );
    productFormData.append(
      "+ Rug Pad",
      formatMoney(addOnsPricing.rugPadPrice).toString()
    );
    productFormData.append("Expected to ship in", await getLeadTime());

    return productFormData;
  }

  function getFormSubmitButton(form) {
    return form.querySelector('[type="submit"]');
  }

  function getProductForm(product) {
    const productForms = document.querySelectorAll("form[is='product-form']"); // "form[action='cart/add']" may be more portable between themes
    for (const form of productForms) {
      const formId = form.attributes.id.value;
      if (formId.includes(product.id)) {
        return form;
      }
    }
    throw new Error(`No form for product ID '${product.id}' found`);
  }

  function getPropertiesBool(formData, propertyName) {
    const targetValues = formData.getAll(`properties[${propertyName}]`);
    const lastValue = targetValues.pop();

    return lastValue === "Yes";
  }

  function getActiveVariant(product, formData) {
    const variantId = formData.get("id");
    return product.variants.find(
      (variant) => variant.id.toString() === variantId
    );
  }

  function isCurrentVariantAvailable() {
    const activeVariant = getActiveVariant(mainProduct, new FormData(productForm));
    return !!(activeVariant && activeVariant.available);
  }

  async function getResponseJson(type, response) {
    console.log(`getResponseJson('${type}', {Response})`);
    if (!response || response.status !== 200) {
      throw new ApiError(window.themeVariables.strings.errorApi, {
        cause: {
          status: response?.status || null,
          statusText: response?.statusText || null,
          response: response,
          getResponseJsonType: type,
        },
      });
    }
    try {
      return await response.json();
    } catch (e) {
      console.error(`getResponseJson caught exception:`, e);
      return null;
    }
  }

  async function getProduct() {
    const pagePath = window.location.pathname;
    if (!pagePath.startsWith("/products/")) {
      throw new Error("Not a product page, cannot get Product");
    }
    const response = await fetch(`${pagePath}.js`);
    return await getResponseJson("getProduct", response);
  }

  function displayMainProductError(message) {
    if (!mainProductAlertElement) {
      return;
    }
    const hide = !message || message?.trim().length === 0;
    mainProductAlertElement.innerHTML = message;
    mainProductAlertElement.classList.toggle("visually-hidden", hide);
  }

  function clearMainProductErrors() {
    displayMainProductError("");
  }

  /** Get product options and selections functions **/
  function getColorOption() {
    return document.querySelector(
      ".color-swatch-list > .color-swatch > input:checked"
    );
  }

  function getSelectedColor() {
    let colorOption = getColorOption();
    if (colorOption === undefined || colorOption === null) {
      throw new Error("Color option missing or no option selected");
    }
    return colorOption.value;
  }

  function getFinishingMethodOption() {
    return document.querySelector('[aria-label="Finishing"]');
  }

  function getSelectedFinishingMethod() {
    // Expected binding options: “serging”, “binding”, “widebinding”, “handserging”
    let finishingOption = getFinishingMethodOption();
    if (finishingOption === undefined || finishingOption === null) {
      return 'Serging'; // TODO: confirm default option to pass to API
    }

    return finishingOption.value;
  }

  function getOriginalSizeOption() {
    return document.querySelector('[aria-label="Size"]');
  }

  function getOriginalSize() {
    let sizeOption = getOriginalSizeOption();
    if (sizeOption !== undefined && sizeOption !== null) {
      return sizeOption.value;
    }
    console.log("No size option");
    return sizeOption;
  }

  function getLeadTime() {
    let leadTime = document.querySelector('[class="lead-time-label"]');
    if (leadTime !== undefined && leadTime !== null && leadTime !== "") {
      return leadTime.innerHTML;
    }
    return null;
  }

  // API Errors
  class ApiError extends Error {
    constructor(message) {
      super(message);
      this.name = "ApiError";
    }
  }

  class PriceApiError extends ApiError {
    constructor(message) {
      super(message);
      this.name = "PriceApiError";
    }
  }

  class CustomRugApiError extends ApiError {
    constructor(message) {
      super(message);
      this.name = "CustomRugApiError";
    }
  }

  /** Rug Fabrication API function **/
  async function getRugCalculationPricing(variant, formElement, calculatingTranslation, productTitle) {

    const isRemnant = [variant.option1, variant.option2, variant.option3]
      .some((option) => option && option.toLowerCase() === "remnant");
    if(!isRemnant){
      const formData = new FormData(formElement);
      const hasPristine = getPropertiesBool(
        formData,
        "Add pristine rug treatment"
      );
      const hasPad = getPropertiesBool(formData, "Add compatible rug pad");
      const variantSku = await prepVariantSkuForFabricationCalc(variant.sku);
      const { length, width } = getDimensions(formData);
      let finishingMethod = getSelectedFinishingMethod(formElement);

      // Transform to comply with API's expected values
      finishingMethod = finishingMethod.replace(/\s/g, "");

      console.log("SKU passed to Rug Fabrication API: " + variantSku);
      pdpPriceElement.innerText = calculatingTranslation;
      const { HasSFPricing, CalculationValues, CalculationCharge } =
        await requestRugCalculation(
          variantSku,
          finishingMethod,
          width,
          length,
          hasPristine,
          hasPad,
          productTitle,
          variant.title
        );
      clearMainProductErrors();
      rugPrice =
        parseFloat(CalculationCharge.RetailCharge.CarpetCharge) +
        parseFloat(CalculationCharge.RetailCharge.FabricationCharge);
      
      if(HasSFPricing) {
        rugPrice =
          parseFloat(CalculationCharge?.RetailSFCharge?.CarpetCharge) +
          parseFloat(CalculationCharge?.RetailSFCharge?.FabricationCharge);

        if(isB2b) {
          rugPrice =
            parseFloat(CalculationCharge?.TradeSFCharge?.CarpetCharge) +
            parseFloat(CalculationCharge?.TradeSFCharge?.FabricationCharge);
        }
      } else if (isB2b) {
        rugPrice =
          parseFloat(CalculationCharge.TradeCharge.CarpetCharge) +
          parseFloat(CalculationCharge.TradeCharge.FabricationCharge);
      }
      const { Total: pristinePrice } = CalculationValues.find((value) => value.Type === "Pristine") ?? {};
      const { Total: paddingPrice } = CalculationValues.find((value) => value.Type === "Padding") ?? {};

      updateCustomPrices(rugPrice, pristinePrice, paddingPrice);
      return { rugPrice, pristinePrice, paddingPrice };
    } else { // Remnant here
      const selectedRemnant = document.querySelector('#remnant-selection-combo-box .combo-box__option-item[aria-selected="true"]');
      const selectedRemnantPrice = selectedRemnant.dataset.itemPrice;
      const rugPrice = parseFloat(selectedRemnantPrice);
      const pristinePrice = parseFloat(0);
      const paddingPrice = parseFloat(0);

      updateCustomPrices(rugPrice, pristinePrice, paddingPrice);
      return { rugPrice, pristinePrice, paddingPrice };
    }
  }

  // NOTE: May need to set default values for specific listeners:
  // i.e. our custom Product Variant submit "click" listener and the theme "variant:changed"
  async function requestRugCalculation(
    sku,
    finishingMethod,
    width,
    length,
    hasPristine,
    hasPad,
    productTitle,
    variantTitle
  ) {

    if (finishingMethod === "Remnant") {
      return {
        CalculationCharge: {
          RetailCharge: {
            TotalCharge: 10,
            CarpetCharge: 149.99,
            CarpetChargeUnitPrice: 0,
            FabricationCharge: 0,
            CalculationValues: [
              { Type: "Padding",           MEA: 0,    UnitPrice: 0,      Total: 0,    Description: "" },
              { Type: "Backing",           MEA: 0,    UnitPrice: 0,      Total: 0,    Description: "" },
              { Type: "Pristine",          MEA: 0,    UnitPrice: 0,      Total: 0,    Description: "" },
              { Type: "Packaging",         MEA: 0,    UnitPrice: 0,      Total: 0,    Description: "" },
              { Type: "SpreadCharge",      MEA: 1,    UnitPrice: 0,      Total: 0,    Description: "Spread Price Charge (0 for Portal and 175 for OES)" },
              { Type: "Disposal",          MEA: 0,    UnitPrice: 0,      Total: 0,    Description: "" },
              { Type: "Binding",           MEA: 220,  UnitPrice: 0.0125, Total: 2.75, Description: "NC COTTON SERGING | 29102" },
              { Type: "BindingArea",       MEA: 7.33, UnitPrice: 0,      Total: 0,    Description: "" },
              { Type: "BorderCharge",      MEA: 0,    UnitPrice: 0,      Total: 0,    Description: "" },
              { Type: "AdditionalFinishing", MEA: 0,  UnitPrice: 0,      Total: 0,    Description: "" },
            ],
          },
          TradeCharge: {
            TotalCharge: 0,
            CarpetCharge: 0,
            CarpetChargeUnitPrice: 0,
            FabricationCharge: 0,
            CalculationValues: [
              { Type: "Padding",           MEA: 0, UnitPrice: 0,   Total: 0, Description: "" },
              { Type: "Backing",           MEA: 0, UnitPrice: 0,   Total: 0, Description: "" },
              { Type: "Pristine",          MEA: 0, UnitPrice: 0,   Total: 0, Description: "" },
              { Type: "Packaging",         MEA: 0, UnitPrice: 0,   Total: 0, Description: "" },
              { Type: "SpreadCharge",      MEA: 0, UnitPrice: 0,   Total: 0, Description: "Spread Price Charge (0 for Portal and 175 for OES)" },
              { Type: "Disposal",          MEA: 0, UnitPrice: 0,   Total: 0, Description: "" },
              { Type: "Binding",           MEA: 0, UnitPrice: 0.0, Total: 0, Description: "NC COTTON SERGING | 29102" },
              { Type: "BindingArea",       MEA: 0, UnitPrice: 0,   Total: 0, Description: "" },
              { Type: "BorderCharge",      MEA: 0, UnitPrice: 0,   Total: 0, Description: "" },
              { Type: "AdditionalFinishing", MEA: 0, UnitPrice: 0, Total: 0, Description: "" },
            ],
          },
          BillToCharge: {
            TotalCharge: 0,
            CarpetCharge: 0,
            CarpetChargeUnitPrice: 0,
            FabricationCharge: 0,
            CalculationValues: [],
          },
        },
        YardAge: 0,
        CutYardAge: 0,
        BalanceWidthInInches: 0,
        BalanceLengthInInches: 0,
        CanCalculate: true,
        TotalCarpetLengthInInches: 0,
        MaxSizeCarpet: {
          HasCarpetWithPattern: false,
          HasCarpetWithoutPattern: false,
          HasCarpetWithSameMethod: false,
          MaxSizeCarpetWithPattern:                  { WidthInInches: 0, LengthInInches: 0 },
          MaxSizeCarpetWithoutPattern:               { WidthInInches: 0, LengthInInches: 0 },
          MaxSizeCarpetWithTheSameMethod:            { WidthInInches: 0, LengthInInches: 0 },
          MaxSizeCarpetWithTheSameMethodWithPattern:    { WidthInInches: 0, LengthInInches: 0 },
          MaxSizeCarpetWithTheSameMethodWithoutPattern: { WidthInInches: 0, LengthInInches: 0 },
        },
        CalculationValues: [
          { Type: "Padding", MEA: 0, UnitPrice: 0, Total: 0, Description: "" },
          { Type: "Pristine", MEA: 0, UnitPrice: 0, Total: 0, Description: "" },
        ],
      };
    }

    const request = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        sku: sku.replace("-", " "),
        bindingMethod: finishingMethod,
        widthInInches: width,
        lengthInInches: length,
        isPristineTreatment: hasPristine,
        isAddingPadding: hasPad,
        productTitle: productTitle,
        variantTitle: variantTitle,
      }),
    };

    try {
      const calcResponse = await fetch(
        "/apps/stark-carpet/rug-calculation",
        request
      ).then(
        async (response) =>{
          validateCustomDimensionsInput();
          return await getResponseJson("requestRugCalculation() fetch", response)
        }
      );
      if (calcResponse === null) {
        throw new Error(`Request Rug Calculation null response exception`);
      }
      console.log("Response from Fabrication Calculator API:");
      console.log(calcResponse);
      applySaleOverrides(calcResponse);
      return calcResponse;
    } catch (e) {
      throw new PriceApiError(window.themeVariables.strings.errorPriceApi, {
        cause: e,
      });
    }
  }

  function applySaleOverrides(calcResponse) {
    const pristineSale = getAddOnSaleData("pristine");
    const rugPadSale = getAddOnSaleData("rug_pad");

    const values = calcResponse?.CalculationValues;
    if (!Array.isArray(values)) return;

    const pristineChecked = document.querySelector(".pristine-treatment__input")?.checked;
    const rugPadChecked = document.querySelector(".rug-pad__input")?.checked;

    if (pristineSale?.is_sale && pristineChecked) {
      const entry = values.find((v) => v.Type === "Pristine");
      if (entry) {
        entry.Total = pristineSale.sale_price ?? 0;
        entry.Description = pristineSale.sale_description ?? "";
        entry.isSale = true;
      }
    }
    if (rugPadSale?.is_sale && rugPadChecked) {
      const entry = values.find((v) => v.Type === "Padding");
      if (entry) {
        entry.Total = rugPadSale.sale_price ?? 0;
        entry.Description = rugPadSale.sale_description ?? "";
        entry.isSale = true;
      }
    }
  }

  async function prepVariantSkuForFabricationCalc(rawSku) {
    // Transform sku as needed for Fabrication Calculator API
    return rawSku
      .replace("_Binding", "")
      .replace("_Wide-Binding", "")
      .replace("_Serging", "")
      .replace("_Hand-Serging", "");
  }

  async function requestXhr(path, formData) {
    const config = {
      method: "POST",
      headers: {
        "ngrok-skip-browser-warning": true,
        "X-Requested-With": "XMLHttpRequest",
        Accept: `application/json`,
        "x-shop-domain":
          window.Shopify.shop === "starkcarpet.myshopify.com"
            ? "starkcarpet.com"
            : "stark-carpet-development.myshopify.com",
      },
      body: formData,
    };

    return await fetch(path, config);
  }

  function updateAvailableLocations(formElement) {
    const updatePaymentContainer = (paymentContainer, associatedFormElement) => {
      if (!paymentContainer || !paymentContainer._updateAvailableLocations || !associatedFormElement) return;
      const variantIdInput = associatedFormElement.querySelector('input[name="id"]');
      if (variantIdInput && variantIdInput.value) {
        paymentContainer._updateAvailableLocations({ id: variantIdInput.value });
      }
    };
    if (formElement && formElement.id) {
      const paymentContainers = document.querySelectorAll(`product-payment-container[form-id="${formElement.id}"]`);
      paymentContainers.forEach((paymentContainer) => {
        updatePaymentContainer(paymentContainer, formElement);
      });
      return;
    }
    const paymentContainers = document.querySelectorAll('product-payment-container');
    paymentContainers.forEach((paymentContainer) => {
      const associatedFormElement = document.getElementById(paymentContainer.getAttribute('form-id'));
      updatePaymentContainer(paymentContainer, associatedFormElement);
    });
  }

  function updateVariantData(){
    const initialSelections = document.querySelectorAll('[data-initial-selection]');
    if(!initialSelections) return;

    initialSelections.forEach(selection => {
      selection.removeAttribute('data-initial-selection');
      selection.removeAttribute('aria-disabled');
    });
    validateSelections();
    updateAvailableLocations();
  }

  /* Sample Ordering functions */
  function updateSampleOrderingOptions() {
    const hideClass = "visually-hidden";
    const isB2bEl = document.querySelector(".customer-type-data");
    const isB2b = isB2bEl !== undefined ? isB2bEl.innerHTML === "true" : false;
    const orderSampleCTA = document.querySelector(
      ".product-form__sample-rug-button"
    );
    const currentColorWaysContainer = document.querySelector('.color-swatch__radio[checked=checked]');
    const currentColorways = currentColorWaysContainer ? currentColorWaysContainer.value : null;
    let variantSamplesDataEl = document.querySelector(".variant-samples-data");
    let variantSamplesData =
      variantSamplesDataEl !== undefined ? variantSamplesDataEl.innerHTML : "";
    let allVariantSamples = variantSamplesData.split("|");
    let thisVariantSamples = [];

    Object.keys(allVariantSamples).forEach((i) => {
      if (allVariantSamples[i] !== "") {
        let currentVariantSamples = allVariantSamples[i].split(":");
        let currentVariantId = currentVariantSamples[0];
        let thisVariantSampleXData = currentVariantSamples[1].split(",");
        let thisVariantSku = thisVariantSampleXData[0];
        let thisVariantAvailability = thisVariantSampleXData[1] === "true";
        let thisVariantColorways = thisVariantSampleXData[2];
        // If available and visible to the customer type (B2B or B2C), we'll show the Order a Sample button
        if (
          thisVariantAvailability === true &&
          currentColorways === thisVariantColorways
        ) {
          if (thisVariantSku.indexOf("_b2b") !== -1 && isB2b) {
            thisVariantSamples.push(currentVariantId);
          } else if (thisVariantSku.indexOf("_b2b") === -1 && !isB2b) {
            thisVariantSamples.push(currentVariantId);
          }
        }
      }
    });
    // Show / Hide "Order a Sample" button as appropriate on variant change
    if (thisVariantSamples.length > 0) {
      orderSampleCTA.classList.toggle(hideClass, false);
    } else {
      orderSampleCTA.classList.toggle(hideClass, true);
    }
  }

  /* Handle Notify Me button visibility on initial load*/
  function updateSampleNotifyMe() {
    const quickBuyDrawer = document.querySelector('quick-buy-drawer');
    const addToCartButtonElement = document.querySelector('#AddToCart[data-show-samples-only=false]');
    const addToCartText = addToCartButtonElement.querySelector('.loader-button__text');
    const notifyMeButtonElement = document.querySelector('#NotifyMe[data-show-samples-only=false]');
    const pdpInitialSelection = document.querySelector('data-initial-selection');

    const hideClass = "visually-hidden";

    const updateNotifyMeVisibility = (atc, notifyMe, atcText, isQuickBuyDrawer, leadTimeDispayEl) => {
      if(atc && notifyMe) {
        if(atcText.textContent.startsWith(window.themeVariables.strings.productFormSoldOut)){
          if (!atc.hasAttribute('disabled')) {
            atc.disabled = true;
          }
          if(isQuickBuyDrawer && leadTimeDisplayEl) {
            leadTimeDisplayEl.classList.toggle(hideClass,true);
          }
          notifyMe.classList.toggle(hideClass,false);
          notifyMe.removeAttribute('tabindex');
        } else {
          if(isQuickBuyDrawer && leadTimeDisplayEl) {
            leadTimeDisplayEl.classList.toggle(hideClass,false);
            if (atc.hasAttribute('disabled')) {
              atc.disabled = false;
            }
          }
          notifyMe.classList.toggle(hideClass,true);
          notifyMe.setAttribute('tabindex', '-1');
        }
      }
    }

    if(!pdpInitialSelection){
      const leadTimeDisplayEl = document.querySelector(".lead-time-label:not(quick-buy-drawer *)");
      updateNotifyMeVisibility(addToCartButtonElement, notifyMeButtonElement, addToCartText, false, leadTimeDisplayEl);
    } else if(quickBuyDrawer && quickBuyDrawer.hasAttribute('open')){ 
      const quickBuyAddToCartButtonElement = document.querySelector('quick-buy-drawer #AddToCart[data-show-samples-only=true]');
      const quickBuyAddToCartText = quickBuyAddToCartButtonElement.querySelector('.loader-button__text');
      const quickBuyNotifyMeButtonElement = document.querySelector('quick-buy-drawer #NotifyMe[data-show-samples-only=true]');
      const leadTimeDisplayEl = document.querySelector("quick-buy-drawer .lead-time-label");
      updateNotifyMeVisibility(quickBuyAddToCartButtonElement, quickBuyNotifyMeButtonElement, quickBuyAddToCartText, true, leadTimeDisplayEl);
    } 
  }

  function initProductTabs() {
    document.addEventListener('DOMContentLoaded', function() {
      const tabs = document.querySelectorAll('.product-tabs__tab');
      const contents = document.querySelectorAll('.product-tabs__content');
      
      // Function to activate a tab
      let previousFinishingValue = null;
      let previousPdpPrice = null;

      // Precut remnants require an explicit color + dropdown selection before the ATC button unlocks
      function updateRemnantAtcState() {
        const remnantsTabActive = document.getElementById('tab-remnants');
        if (!remnantsTabActive || remnantsTabActive.getAttribute('aria-selected') !== 'true') {
          return;
        }

        const checkedColor = document.querySelector('input[name="remnant-color-filter"]:checked');
        const needsColor = document.querySelectorAll('input[name="remnant-color-filter"]').length > 0;
        const colorChosen = !needsColor || !!checkedColor;

        const selectedItem = document.querySelector('#remnant-selection-combo-box .combo-box__option-item[aria-selected="true"]');
        const itemChosen = !!selectedItem && !selectedItem.disabled && (!needsColor || !checkedColor || selectedItem.dataset.itemColor === checkedColor.value);

        if (colorChosen && itemChosen) {
          if (!atcButton.hasAttribute('aria-busy')) {
            atcButton.removeAttribute('disabled');
          }
        } else {
          atcButton.setAttribute('disabled', 'disabled');
        }
      }

      function activateTab(targetTab, focusTab = false) {
        const customSizeWidthFt = document.querySelector('[name="customSizeWidthFt"]');
        const customSizeWidthIn = document.querySelector('[name="customSizeWidthIn"]');
        const customSizeLengthFt = document.querySelector('[name="customSizeLengthFt"]');
        const customSizeLengthIn = document.querySelector('[name="customSizeLengthIn"]');

        // Always clear ATC price on any tab switch
        const freshAtcPrice = document.querySelector('#AddToCart .loader-button__text span.atc-price')
                           || document.querySelector('#AddToCart span.atc-price');
        if (freshAtcPrice) freshAtcPrice.innerHTML = '';

        if (targetTab !== 'remnants') {
          const skuEl = document.querySelector('[data-product-sku-number]');
          if (skuEl) {
            delete skuEl.dataset.remnantSku;
            delete skuEl.dataset.remnantPrice;
            const finishingOpt = document.querySelector('[aria-label="Finishing"]');
            if (finishingOpt) delete finishingOpt.dataset.remnantOverride;
          }
          const finishingSelectLeave = document.querySelector('[aria-label="Finishing"]');
          const finishingComboLeave = finishingSelectLeave ? finishingSelectLeave.closest('combo-box') : null;
          if (finishingComboLeave) {
            const remnantOpt = finishingComboLeave.querySelector('.combo-box__option-item[value="Remnant"]');
            if (remnantOpt) remnantOpt.style.display = 'none';
          }
          if (previousFinishingValue !== null && finishingComboLeave) {
            const prevBtn = finishingComboLeave.querySelector('.combo-box__option-item[value="' + previousFinishingValue + '"]');
            if (prevBtn) prevBtn.click();
            previousFinishingValue = null;
          }
          if (customSizeWidthFt) customSizeWidthFt.value = '';
          if (customSizeWidthIn) customSizeWidthIn.value = '';
          if (customSizeLengthFt) customSizeLengthFt.value = '';
          if (customSizeLengthIn) customSizeLengthIn.value = '';
          // Restore saved PDP price, or fall back to starting price
          if (pdpPriceElement) {
            if (previousPdpPrice) {
              pdpPriceElement.innerHTML = previousPdpPrice;
            } else {
              const startingPrice = getCustomPricing(variantPricing.price);
              pdpPriceElement.innerHTML = startingPrice ? formatMoney(startingPrice) + ' USD' : '';
            }
            previousPdpPrice = null;
          }
        } else {
          const finishingSelectEnter = document.querySelector('[aria-label="Finishing"]');
          const finishingComboEnter = finishingSelectEnter ? finishingSelectEnter.closest('combo-box') : null;
          if (finishingSelectEnter) previousFinishingValue = finishingSelectEnter.value;
          // Save current PDP price before entering remnants tab
          if (pdpPriceElement) previousPdpPrice = pdpPriceElement.innerHTML || null;
          if (finishingComboEnter) {
            const remnantOpt = finishingComboEnter.querySelector('.combo-box__option-item[value="Remnant"]');
            if (remnantOpt) remnantOpt.style.display = '';
            if (remnantOpt) remnantOpt.click();
          }
          // Clear PDP price until a remnant is selected
          if (pdpPriceElement) pdpPriceElement.innerHTML = '';
          const selectedRemnantOption = document.querySelector('#remnant-selection-combo-box select option:checked');
          if (selectedRemnantOption && selectedRemnantOption.dataset.itemSize) {
            const itemSize = JSON.parse(selectedRemnantOption.dataset.itemSize);
            if (customSizeWidthFt) customSizeWidthFt.value = itemSize.width.feet;
            if (customSizeWidthIn) customSizeWidthIn.value = itemSize.width.inches === 0 ? '' : itemSize.width.inches;
            if (customSizeLengthFt) customSizeLengthFt.value = itemSize.length.feet;
            if (customSizeLengthIn) customSizeLengthIn.value = itemSize.length.inches === 0 ? '' : itemSize.length.inches;
          }
        }
        const targetContent = document.getElementById(targetTab + '-content');
        
        // Update all tabs (keep them tabbable)
        tabs.forEach(t => {
          t.classList.remove('active');
          t.setAttribute('aria-selected', 'false');
        });
        
        // Update all content panels
        contents.forEach(c => {
          c.classList.remove('active');
          c.setAttribute('aria-hidden', 'true');
          c.setAttribute('tabindex', '-1');
        });
        
        // Activate selected tab
        const activeTab = document.querySelector(`[data-tab="${targetTab}"]`);
        if (activeTab) {
          activeTab.classList.add('active');
          activeTab.setAttribute('aria-selected', 'true');
          
          if (focusTab) {
            activeTab.focus();
          }
        }
        
        // Activate corresponding content
        if (targetContent) {
          targetContent.classList.add('active');
          targetContent.setAttribute('aria-hidden', 'false');
          targetContent.setAttribute('tabindex', '0');
        } else {
          console.error('Could not find element with id:', targetTab + '-content');
        }

        updateRemnantAtcState();
      }

      tabs.forEach(tab => {
        // Click handler
        tab.addEventListener('click', function(e) {
          e.preventDefault();
          const targetTab = this.getAttribute('data-tab');
          activateTab(targetTab);
        });
        
        // Keyboard navigation handler
        tab.addEventListener('keydown', function(e) {
          const currentIndex = Array.from(tabs).indexOf(this);
          let targetIndex;
          
          switch(e.key) {
            case 'ArrowRight':
            case 'ArrowDown':
              e.preventDefault();
              targetIndex = (currentIndex + 1) % tabs.length;
              break;
            case 'ArrowLeft':
            case 'ArrowUp':
              e.preventDefault();
              targetIndex = (currentIndex - 1 + tabs.length) % tabs.length;
              break;
            case 'Home':
              e.preventDefault();
              targetIndex = 0;
              break;
            case 'End':
              e.preventDefault();
              targetIndex = tabs.length - 1;
              break;
            case 'Enter':
            case ' ':
              e.preventDefault();
              const targetTab = this.getAttribute('data-tab');
              activateTab(targetTab);
              return;
            default:
              return;
          }
          
          if (targetIndex !== undefined) {
            const targetTab = tabs[targetIndex].getAttribute('data-tab');
            activateTab(targetTab, true);
          }
        });
      });

      const remnantColorFilterInputs = document.querySelectorAll('input[name="remnant-color-filter"]');
      if (remnantColorFilterInputs.length > 0) {
        function applyRemnantColorFilter(selectedColor) {
          const comboBox = document.getElementById('remnant-selection-combo-box');
          if (!comboBox) return;
          const buttons = comboBox.querySelectorAll('.combo-box__option-item');
          const remnantSelectionValue = document.getElementById('remnant-selection-value');
          const remnantDefaultLabel = remnantSelectionValue?.dataset.remnantDefaultLabel;
          const remnantAllInCartLabel = remnantSelectionValue?.dataset.remnantAllItemsInCartLabel;

          let visibleCount = 0;
          let visibleInCartCount = 0;

          buttons.forEach(function(btn) {
            const itemColor = btn.dataset.itemColor;
            const matches = itemColor === selectedColor;
            btn.style.display = matches ? '' : 'none';
            if (matches) {
              visibleCount++;
              if (btn.hasAttribute('disabled')) visibleInCartCount++;
            }
          });

          if (remnantSelectionValue && remnantDefaultLabel) {
            if (visibleCount > 0 && visibleInCartCount === visibleCount) {
              remnantSelectionValue.textContent = remnantAllInCartLabel;
            } else {
              remnantSelectionValue.textContent = remnantDefaultLabel;
            }
          }

          const toggleBtn = document.querySelector('[aria-controls="remnant-selection-combo-box"]');
          if (toggleBtn) {
            if (visibleCount > 0 && visibleInCartCount === visibleCount) {
              toggleBtn.setAttribute('disabled', '');
            } else {
              toggleBtn.removeAttribute('disabled');
            }
          }
        }

        remnantColorFilterInputs.forEach(function(input) {
          input.addEventListener('change', function() {
            const finishingOption = document.querySelector('[aria-label="Finishing"]');
            if (finishingOption) finishingOption.dataset.remnantOverride = 'true';
            applyRemnantColorFilter(this.value);
            const colorLabel = document.getElementById('remnant-color-filter-value');
            if (colorLabel) colorLabel.textContent = this.value.toUpperCase();
            // No remnant selected yet after color change — clear price
            const freshAtcPrice = document.querySelector('#AddToCart .loader-button__text span.atc-price')
                               || document.querySelector('#AddToCart span.atc-price');
            if (freshAtcPrice) freshAtcPrice.innerHTML = '';
            if (pdpPriceElement) pdpPriceElement.innerHTML = '';
            updateRemnantAtcState();
          });
        });

        const initialChecked = document.querySelector('input[name="remnant-color-filter"]:checked');
        if (initialChecked) {
          applyRemnantColorFilter(initialChecked.value);
        }
      }

      // On page load, hide "Remnant" from the finishing dropdown and deselect it if active
      const finishingSelect = document.querySelector('[aria-label="Finishing"]');
      if (finishingSelect) {
        const finishingComboBox = finishingSelect.closest('combo-box');
        if (finishingComboBox) {
          const remnantFinishingOption = finishingComboBox.querySelector('.combo-box__option-item[value="Remnant"]');
          if (remnantFinishingOption) remnantFinishingOption.style.display = 'none';
        }
        if (finishingSelect.value === 'Remnant') {
          const firstVisibleBtn = finishingComboBox
            ? finishingComboBox.querySelector('.combo-box__option-item:not([value="Remnant"])')
            : null;
          if (firstVisibleBtn) firstVisibleBtn.click();
        }
      }

      const remnantCombo = document.getElementById('remnant-selection-combo-box');
      if (remnantCombo) {
        remnantCombo.addEventListener('click', function(e) {
          const btn = e.target.closest('.combo-box__option-item');
          if (btn) {
            const customSizeWidthFt = document.querySelector('[name="customSizeWidthFt"]');
            const customSizeWidthIn = document.querySelector('[name="customSizeWidthIn"]');
            const customSizeLengthFt = document.querySelector('[name="customSizeLengthFt"]');
            const customSizeLengthIn = document.querySelector('[name="customSizeLengthIn"]');
            const itemSize = JSON.parse(btn.dataset.itemSize);
            const itemPrice = btn.dataset.itemPrice;
            
            customSizeWidthFt.value = itemSize.width.feet;
            customSizeWidthIn.value = itemSize.width.inches == 0 ? '' : itemSize.width.inches;
            customSizeLengthFt.value = itemSize.length.feet;
            customSizeLengthIn.value = itemSize.length.inches == 0 ? '' : itemSize.length.inches;

            const finishingOption = document.querySelector('[aria-label="Finishing"]');
            if (finishingOption) finishingOption.dataset.remnantOverride = 'true';

            validateCustomDimensionsInput();
            updateCustomPrices(itemPrice,0,0);

            const remnantItemSku = btn.dataset.itemSku;
            const skuEl = document.querySelector('[data-product-sku-number]');
            if (skuEl && remnantItemSku) {
              skuEl.dataset.remnantSku = remnantItemSku;
              skuEl.dataset.remnantPrice = itemPrice || '';
              skuEl.textContent = remnantItemSku;
            }

            updateRemnantAtcState();
          }
        });
      }
    });
  }
})();

// Sticky Header Hide/Show on Scroll
(function() {
  const enableStickyHeader = getComputedStyle(document.documentElement).getPropertyValue('--enable-sticky-header').trim();
  if (enableStickyHeader !== '1') return;
  
  const headerGroup = document.getElementById('header-group');
  const brandTabsSection = document.querySelector('.shopify-section--brand-tabs');
  
  if (!headerGroup || !brandTabsSection) return;
  
  let lastScrollY = window.scrollY;
  let maxTranslate = 0;
  let currentTranslate = 0;
  let ticking = false;
  
  function calculateHeights() {
    const announcementBar = document.querySelector('.shopify-section--announcement-bar');
    const isMobile = window.innerWidth <= 1000;
    
    if (isMobile) {
      const announcementBarHeight = announcementBar ? announcementBar.offsetHeight : 0;
      const brandTabsHeight = brandTabsSection ? brandTabsSection.offsetHeight : 0;
      maxTranslate = announcementBarHeight + brandTabsHeight;
    } else {
      maxTranslate = announcementBar ? announcementBar.offsetHeight : 0;
    }
  }
  
  function updateHeaderPosition() {
    const currentScrollY = window.scrollY;
    const scrollDelta = currentScrollY - lastScrollY;
    
    if (scrollDelta > 0) {
      currentTranslate = Math.min(currentTranslate + scrollDelta, maxTranslate);
    } else if (scrollDelta < 0) {
      currentTranslate = Math.max(currentTranslate + scrollDelta, 0);
    }
    
    headerGroup.style.top = `-${currentTranslate}px`;
    
    lastScrollY = currentScrollY;
    ticking = false;
  }
  
  function requestTick() {
    if (!ticking) {
      requestAnimationFrame(updateHeaderPosition);
      ticking = true;
    }
  }
  
  calculateHeights();
  window.addEventListener('resize', calculateHeights);
  window.addEventListener('scroll', requestTick, { passive: true });
})();

// Fix flickity page dots aria-current attribute
(function() {
  function fixAriaCurrentInDots() {
    const pageDots = document.querySelectorAll('.flickitylf-page-dots .dot[aria-current="step"]');
    pageDots.forEach(dot => {
      console.log('Changing aria-current from "step" to "true"');
      dot.setAttribute('aria-current', 'true');
    });
  }

  // Run on load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fixAriaCurrentInDots);
  } else {
    fixAriaCurrentInDots();
  }

  // Watch for changes using MutationObserver
  const observer = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      if (mutation.type === 'attributes' && mutation.attributeName === 'aria-current') {
        const target = mutation.target;
        if (target.classList.contains('dot') && target.getAttribute('aria-current') === 'step') {
          console.log('MutationObserver: Fixing aria-current from "step" to "true"');
          target.setAttribute('aria-current', 'true');
        }
      }
    });
  });

  // Observe all flickity page dots containers
  const observePageDots = () => {
    const pageDotContainers = document.querySelectorAll('.flickitylf-page-dots');
    console.log('Observing page dots containers:', pageDotContainers.length);
    pageDotContainers.forEach(container => {
      observer.observe(container, {
        attributes: true,
        attributeFilter: ['aria-current'],
        subtree: true
      });
    });
  };

  // Initial observation
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observePageDots);
  } else {
    observePageDots();
  }

  // Re-observe when new content is added - run multiple times
  setTimeout(observePageDots, 500);
  setTimeout(observePageDots, 1000);
  setTimeout(observePageDots, 2000);
  
  // Also fix periodically
  setInterval(fixAriaCurrentInDots, 1000);
})();

/**
 * Mobile menu toggle
 *
 * This handler keeps the toggle working by flipping the controlled drawer's
 * `open` state. It reads aria-controls at click time so it respects the brand
 * switcher dynamically changing which drawer the button controls.
 */
(function () {
  document.addEventListener('click', function (event) {
    const toggle = event.target.closest('#mobile-menu-toggle');
    if (!toggle) {
      return;
    }

    event.preventDefault();

    const drawer = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!drawer) {
      return;
    }

    drawer.open = !drawer.open;
  });
})();
